import _findIndex from 'lodash.findindex';
import cloneDeep from 'lodash.clonedeep';
import { Collider2d } from 'collider2d';
import { defineStore } from 'pinia';
import {
  LaserConfiguration,
  Personalization,
  PmsColor,
  ProductPrintArea,
} from '@midocean/webapps-shared-domain';
import {
  ArtworkPayload,
  LogoShape,
  TextShape,
  VariantArtworkImages,
} from '@/web-components/import-wizard/typings/pintura';
import { v4 as uuidv4 } from 'uuid';
import { convertToBlob, mm2px } from '@/utils/common';
import { SmallDetailsPayload } from '@/typings';
import { useStore as useProductStore } from './product';
import { useStore as useActionsStore } from './action';
import customersAPI from '@/services/CustomersService';
import { useStore as useMirrorStore } from './mirror';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import {
  AnnotationShape,
  PrintArea,
  RectangleArea,
  Text,
} from '@/web-components/import-wizard-v2/typings/common';
import {
  getShapePositionInAreainPt,
  getShapePositionOnProductInMM,
} from '@/utils/areaHelper';
import {
  ProcessTextCommand,
  TextAlignment,
  Product3DPreviewCommand,
  ProductAIPreviewCommand,
} from '@/typings/SmallDetails';
import FileService from '@/services/FileService';
import { base64StringToBlob } from 'blob-util';
import { positionText } from '@/utils/alignTextV2';
import { DefaultSmallDetailsResult } from '@/typings/SmallDetails';
import { useStore as useShapeBoxSectionStore } from '../shapeBoxSection';
import { useStore as useColorsManagementStore } from '@/stores/v2/colorsManagement';
import {
  alignAnnotationShape,
  alignRoundItemAnnotationShape,
} from '@/utils/alignmentHelper';
import { lineWidthV2 } from '@/constants/areaLineWidth';
import {
  createShapeFromPayload,
  getCenterShapePosition,
  getShapePositionInPX,
} from '@/utils/shapeHelper';
import {
  ChangeColorCommand,
  RemoveColorCommand,
  RemoveColorResult,
} from '@/web-components/import-wizard/typings';
import {
  determineNewWidthPx,
  determineNewHeightPx,
  determineNewWidthMm,
  determineNewHeightMm,
} from '@/utils/common';
import {
  ChangeColorsCommand,
  DoRoundCroppingCommand,
  SetArtworkBackgroundCommand,
} from '@/web-components/import-wizard/typings/colorManagement/colorManagement';
import { checkIsInAreaV2 } from '@/utils/collisionHelper';
import { ProductPrintVariant } from '@midocean/webapps-shared-domain';
import { ShapeSnapshot } from '@/typings/historyV2';
import { ProductPrintPanel } from '@/typings/ProductPrintData';
import { mapSmallDetailsPayload } from '@/components/SmallDetails/utils/helper';
import { CreatePersonalizationFormWrapper } from '@/web-components/import-wizard/typings/common/createPersonalization';
import { EMBROIDERY_CONVERTER_ACTIVE } from '@/constants/featureFlags';

import { recalculateFontSizeInPt } from '@/utils/textHelper';

export const nonTextShapes = (shape: AnnotationShape): boolean =>
  shape.kind === 'Logo';
export const textShapes = (shape: AnnotationShape): boolean =>
  shape.kind === 'Text';

function calculateDpi(
  dimensionInPixels: number,
  dimensionInMM: number
): number {
  const inches = dimensionInMM / 25.4;
  return dimensionInPixels / inches;
}

export const useStore = defineStore({
  id: 'shapeV2',

  state: () => {
    return {
      activeShapeId: '',
      errors: [],
      collider2d: new Collider2d(),
      shapes: [] as AnnotationShape[],
      positionShapeMap: [] as Record<string, string>[],
      personalization: {} as Personalization,
      isMoccaPersonalization: false,
    };
  },
  getters: {
    positionShapes(state) {
      return (positionId: string) => {
        if (positionId) {
          return state.shapes.filter((s) =>
            state.positionShapeMap.some(
              (psm) => psm.id === positionId && psm.logoId === s.id
            )
          );
        }
        return [];
      };
    },
    shapeById: (state) => {
      return (shapeId: string) => {
        return state.shapes.find((s) => s.id === shapeId);
      };
    },
    transparentLogoExists: (state) => {
      return state.shapes.some(
        (s) => s.kind === 'Logo' && s.pmsColorsInPngFile.length === 0
      );
    },
  },
  actions: {
    async getPersonalization(id: string) {
      try {
        const { data } = await customersAPI.getPersonalization(id);
        this.personalization = data;
      } catch (e) {
        console.log(e);
      }
    },

    reassignRefs({ from, to }: { from: string; to: string }) {
      if (from === to) {
        return;
      }
      const reassignedFromTo = this.positionShapeMap.map((psm) => {
        return psm.id === from ? { ...psm, id: to } : psm;
      });

      this.positionShapeMap = reassignedFromTo;
    },

    async replicateShape(shape: AnnotationShape, positionId: string) {
      const productStore = useProductStore();
      const scaledArea = productStore.printPositionArea(positionId, true);
      const productArea = productStore.activePosition.printArea;

      if (scaledArea && productStore.activePosition && productArea) {
        this.positionShapeMap.push({
          id: positionId,
          logoId: shape.id,
        });

        const newShapePosition = {
          x: shape.x,
          y: shape.y,
          width: shape.width,
          height: shape.height,
        };
        const positionOnAreaInPt = getShapePositionInAreainPt(
          newShapePosition,
          scaledArea
        );

        const productArea = productStore.activePosition.printArea;

        if (positionOnAreaInPt) {
          const positionOnProductInMM = getShapePositionOnProductInMM(
            positionOnAreaInPt,
            productArea,
            newShapePosition.width / newShapePosition.height
          );

          if (positionOnProductInMM) {
            shape.xOnProductMM = positionOnProductInMM?.xOnProductMM;
            shape.yOnProductMM = positionOnProductInMM?.yOnProductMM;
          }
        }

        const colorManagementStore = useColorsManagementStore();
        if (
          shape.kind === 'Logo' &&
          colorManagementStore.lastPalette !== 'ALL'
        ) {
          colorManagementStore.changeToClosestPantonesFromPalette(shape);
        }

        this.shapes.push(cloneDeep(shape));

        const mirrorStore = useMirrorStore();
        const mirrorSettings = mirrorStore.positionMirrorSettings.find(
          (x) => x.positionId === positionId
        );
        if (mirrorSettings && mirrorSettings.isMirrorApplied) {
          mirrorStore.addMirrorShapeToPosition(shape, positionId);
        }
      }
    },

    async recreateTextPdfs() {
      const targets: TextShape[] = this.shapes.filter(
        (shape) => shape.kind === 'Text' && shape.triggerPdfGeneration
      ) as TextShape[];

      const tasks = targets.map((shape) =>
        this.textToImage(
          shape.w2pText.fontFamily,
          shape.w2pText.fontSizePT,
          shape.w2pText.fontWeight,
          shape.w2pText.fontStyle,
          shape.w2pText.text,
          shape.w2pText.textAlign,
          shape.w2pText.colorName,
          shape.w2pText.textHtml,
          shape.w2pText.color,
          false,
          shape.id
        )
      );

      await Promise.all(tasks);
    },

    async addArtworkPayload(
      artworkPayload: ArtworkPayload,
      positionId: string
    ) {
      const productStore = useProductStore();
      const printableAreaRotation = productStore.rotation;
      const scaledArea = productStore.printPositionArea(positionId, true);
      const activePosition = productStore.getProductPrintPosition(positionId);
      const productArea = activePosition?.printArea;
      const unavailablePrintPosition =
        productStore.unavailablePrintPositions.find(
          (x) => x.positionId === positionId
        );

      if (scaledArea && activePosition && productArea) {
        const polygonDetails = productStore.polygonDetails(activePosition.id);
        const shapePayload = createShapeFromPayload(
          artworkPayload,
          scaledArea,
          printableAreaRotation,
          activePosition,
          polygonDetails
        );

        this.shapes.push(shapePayload);
        this.selectShape(shapePayload.id);
        this.positionShapeMap.push({
          id: positionId,
          logoId: shapePayload.id,
        });

        this.checkIsShapeInArea(shapePayload.id);

        const colorManagementStore = useColorsManagementStore();

        if (
          shapePayload.kind === 'Logo' &&
          colorManagementStore.lastPalette !== 'ALL'
        ) {
          colorManagementStore.changeToClosestPantonesFromPalette(shapePayload);
        }

        this.updateShapePosition(shapePayload, scaledArea, productArea);

        const isTraditionalTechnique =
          productStore.isTraditionalTechnique(positionId);

        await this.updateShapeImages(shapePayload.id, isTraditionalTechnique);

        if (unavailablePrintPosition?.technique.id) {
          await this.applyEffectIfNeeded(
            positionId,
            unavailablePrintPosition?.technique.id,
            shapePayload
          );
        }
      }
    },

    resetShapeStateToDefault() {
      this.$patch((state) => {
        state.activeShapeId = '';
        state.errors = [];
        state.collider2d = new Collider2d();
        state.shapes = [];
        state.positionShapeMap = [];
        state.personalization = {} as Personalization;
      });
    },

    setIsMoccaPersonalization(isMoccaPersonalization: boolean) {
      this.isMoccaPersonalization = isMoccaPersonalization;
    },

    findClosestColor(existingChannels: PmsColor[], detectedColor: PmsColor) {
      let minimumDistance = Number.MAX_SAFE_INTEGER;
      let nameOfClosest = '';
      const colorManagementStore = useColorsManagementStore();
      existingChannels.forEach((channel) => {
        const distance = colorManagementStore.getFuzzDistance(
          channel.red,
          detectedColor.red,
          channel.green,
          detectedColor.green,
          channel.blue,
          detectedColor.blue
        );
        if (distance < minimumDistance) {
          minimumDistance = distance;
          nameOfClosest = channel.name;
        }
      });

      return nameOfClosest;
    },

    async updateColorsAfterImageIsAdjusted(
      shapeId: string,
      doTrimming: boolean
    ) {
      const idx = this.shapes.findIndex((p) => p.id === shapeId);
      const shape = this.shapes[idx];
      if (shape.kind === 'Logo') {
        const { data } = await FileService.detectColors(
          shape.pngFileWithoutConverters,
          doTrimming,
          // Check if the original file is a vector file
          shape.originalHorizontalResolutionDPIs === 0
        );

        const colorManagementStore = useColorsManagementStore();
        colorManagementStore.changeColorsToClosestPantonesFromPalette(
          data.colors
        );

        data.colors.forEach((color) => {
          color.name = this.findClosestColor(shape.pmsColorsInPngFile, color);
        });

        const colors = shape.pmsColorsInPngFile.filter((x) =>
          data.colors.some((color) => color.name === x.name)
        );

        const widthScaleFactor = shape.pngWidthPixels / data.widthInPx;
        const heightScaleFactor = shape.pngHeightPixels / data.heightInPx;
        const newWidth = shape.width / widthScaleFactor;
        const newHeight = shape.height / heightScaleFactor;
        const newWidthMM = shape.widthOnProductMM / widthScaleFactor;
        const newHeightMM = shape.heightOnProductMM / heightScaleFactor;

        this.$patch((state) => {
          const current = state.shapes[idx];
          if (current.kind === 'Logo') {
            state.shapes.splice(idx, 1, {
              ...current,
              pmsColorsInPngFile: colors,
              width: newWidth,
              height: newHeight,
              widthOnProductMM: newWidthMM,
              heightOnProductMM: newHeightMM,
              aspectRatio: newWidth / newHeight,
              pngWidthPixels: data.widthInPx,
              pngHeightPixels: data.heightInPx,
              backgroundImage: URL.createObjectURL(
                base64StringToBlob(data.trimmedImageBase64, 'image/png')
              ),
              pngFile: base64StringToBlob(data.trimmedImageBase64, 'image/png'),
              pngFileWithoutConverters: base64StringToBlob(
                data.trimmedImageBase64,
                'image/png'
              ),
            });
          }
        });
      }
    },

    async updateShapeImages(
      shapeId: string,
      isTraditionalTechniqueSelected: boolean
    ) {
      const shape = this.shapes.find((s) => s.id === shapeId);
      if (
        shape &&
        shape.kind === 'Logo' &&
        shape.traditionalTechniquePngFile &&
        shape.traditionalTechniquePngFile !== ''
      ) {
        // in case of traditional technique - use png file rasterized from cmyk pdf
        if (isTraditionalTechniqueSelected) {
          console.log('Traditional technique images update flow');

          if (shape.traditionalTechniquePngFile instanceof Blob) {
            shape.pngFileWithoutConverters = shape.traditionalTechniquePngFile;
            shape.pngFile = shape.traditionalTechniquePngFile;
            shape.backgroundImage = URL.createObjectURL(
              shape.traditionalTechniquePngFile
            );
          }

          if (typeof shape.traditionalTechniquePngFile === 'string') {
            const traditionalTechniquePngFileBlob = await convertToBlob(
              shape.traditionalTechniquePngFile as string
            );
            shape.pngFileWithoutConverters = traditionalTechniquePngFileBlob;
            shape.pngFile = traditionalTechniquePngFileBlob;
            shape.backgroundImage = URL.createObjectURL(
              traditionalTechniquePngFileBlob
            );
          }
        } else {
          console.log('Full color images update flow');

          // in case of full color technique - use png file rasterized from original file
          shape.pngFileWithoutConverters = shape.fullColorTechniquePngFile;
          shape.pngFile = shape.fullColorTechniquePngFile;
          shape.backgroundImage = URL.createObjectURL(
            shape.fullColorTechniquePngFile
          );
        }

        const shapeIdx = this.shapes.findIndex((p) => p.id === shapeId);
        this.shapes.splice(shapeIdx, 1, shape);
      }
    },

    async alignShape(
      shapeId: string,
      alignmentType: string,
      isFromRecommendedPrintPosition = false
    ) {
      const shape = this.shapes.find((s) => s.id === shapeId);
      const productStore = useProductStore();

      if (shape !== undefined && productStore.activePosition.id) {
        const { width, height } = shape;

        const printableAreaRotation = productStore.rotation;
        const scaledArea = productStore.printPositionArea(
          productStore.activePosition.id,
          true
        );
        const productArea = productStore.activePosition.printArea;
        const polygonDetails = productStore.polygonDetails(
          productStore.activePosition.id
        );

        if (scaledArea) {
          alignAnnotationShape(
            shape,
            scaledArea,
            printableAreaRotation,
            alignmentType,
            width,
            height,
            polygonDetails
          );
          this.updateShapePosition(
            {
              ...shape,
              isOnRecommendedPrintPosition: isFromRecommendedPrintPosition,
            },
            scaledArea,
            productArea
          );
        }
      }
    },

    alignRoundItemShape(
      shapeId: string,
      panelArea: ProductPrintPanel,
      alignmentType: string
    ) {
      const productStore = useProductStore();
      const printableAreaRotation = productStore.rotation;
      const shape = this.shapes.find((s) => s.id === shapeId);

      if (shape !== undefined && productStore.activePosition.id) {
        const scaledArea = productStore.printPositionArea(
          productStore.activePosition.id,
          true
        );
        const productArea = productStore.activePosition.printArea;

        if (scaledArea) {
          const newShapePosition = alignRoundItemAnnotationShape(
            shape,
            panelArea,
            alignmentType,
            scaledArea,
            printableAreaRotation
          );

          this.updateShapePosition(newShapePosition, scaledArea, productArea);
        }
      }
    },

    async initializeStateFromPersonalization() {
      const productStore = useProductStore();
      const actionStore = useActionsStore();

      const printPositions =
        this.personalization.proofs[0].productPrintPositions;

      for (const pos of printPositions) {
        const product = productStore.data.productPrintPositions.find(
          (p) => p.name === pos.name
        );
        const positionID = product?.id;

        if (positionID) {
          const printArea = productStore.getProductPrintPosition(positionID);

          const scaledArea = productStore.printPositionArea(positionID, true);

          const originalDPI = product?.originalImageWebDpi as number;

          let mappedArtworkIds: Record<string, string>[] =
            pos.personalizationArtworks.map((a) => {
              return {
                id: positionID,
                logoId: a.id,
              } as Record<string, string>;
            });

          this.positionShapeMap = [
            ...this.positionShapeMap,
            ...mappedArtworkIds,
          ];
          mappedArtworkIds = [];

          const isLaserConverter = productStore.isLaserConverter(
            positionID,
            pos.productPrintTechnique.sapCode
          );
          const isDebossing = productStore.isDebossingTechnique(
            positionID,
            pos.productPrintTechnique.sapCode
          );

          const isSublimationTechnique = productStore.isSublimationTechnique;

          const isPantoneColorTechnique =
            productStore.isPantoneColorTechnique(positionID);

          if (printArea) {
            for (const a of pos.personalizationArtworks) {
              const imageBlob = await convertToBlob(a.pngFileUrl);

              const shapePosition = getShapePositionInPX(
                printArea.printArea,
                a.distanceFromLeftWithCropping,
                a.distanceFromTopWithCropping,
                a.widthWithCropping,
                a.heightWithCropping,
                scaledArea
              );

              const xInPX = shapePosition.xInPX;
              const yInPX = shapePosition.yInPX;
              const widthInPX = shapePosition.widthInPX;
              const heightInPX = shapePosition.heightInPX;

              let variantArtworkImages: VariantArtworkImages[] = [];

              if (
                isLaserConverter ||
                isDebossing ||
                isPantoneColorTechnique ||
                isSublimationTechnique
              ) {
                variantArtworkImages = await Promise.all(
                  a.variantArtworkImages.map(async (vi) => {
                    const imageBlob = await convertToBlob(
                      vi.variantArtworkPngUrl
                    );
                    return {
                      variantColor: vi.variantColor,
                      variantArtworkPng: imageBlob,
                    } as VariantArtworkImages;
                  })
                );
              }

              let smallDetailsPayload: SmallDetailsPayload | undefined =
                undefined;

              if (DoSmallDetailsPart) {
                const isSmallDetailsTechnique =
                  productStore.isSmallDetailsTechnique(
                    positionID,
                    pos.productPrintTechnique.sapCode
                  );

                if (a.smallDetails) {
                  smallDetailsPayload = {
                    negativeDetailsLoss: a.smallDetails.negativeLoss,
                    positiveDetailsLoss: a.smallDetails.positiveLoss,
                    status: a.smallDetails.status,
                    approvedAt: a.smallDetails.approvedAt
                      ? a.smallDetails.approvedAt.toString()
                      : undefined,
                    isCheckValid: true,
                    pngFile: a.smallDetails.fileUrl,
                    pngFileBlob: await convertToBlob(a.smallDetails.fileUrl),
                    calculatedQuality: a.smallDetails.calculatedQuality,
                    id: a.id,
                  };
                }

                if (isSmallDetailsTechnique && a.smallDetails === null) {
                  smallDetailsPayload = {
                    negativeDetailsLoss: 0,
                    positiveDetailsLoss: 0,
                    status: 'NoCheck',
                    approvedAt: undefined,
                    isCheckValid: true,
                    pngFile: '',
                    pngFileBlob: new Blob(),
                    calculatedQuality: 0,
                    id: a.id,
                  };
                }
              }

              const isTraditionalTechnique =
                productStore.isTraditionalTechnique(positionID);

              const traditionalTechniquePngFile =
                a.traditionalTechniquePngFileUrl
                  ? await convertToBlob(a.traditionalTechniquePngFileUrl)
                  : '';

              const mappedImageBlob =
                isTraditionalTechnique && traditionalTechniquePngFile
                  ? traditionalTechniquePngFile
                  : imageBlob;

              this.shapes.push({
                zindex: a.zIndex,
                isRoundLogo: a.isRoundLogo,
                id: a.id,
                pngFile:
                  variantArtworkImages.find(
                    (v) =>
                      v.variantColor === productStore.selectedVariant.colour
                  )?.variantArtworkPng || mappedImageBlob,
                pngFileWithoutConverters: mappedImageBlob,
                title: a.title !== null ? a.title : '',
                originalFileUrl: a.originalFileUrl,
                originalHorizontalResolutionDPIs:
                  a.originalHorizontalResolutionDPIs,
                originalVerticalResolutionDPIs:
                  a.originalVerticalResolutionDPIs,
                pngHorizontalResolutionDPIs: a.pngHorizontalResolutionDPIs,
                pngVerticalResolutionDPIs: a.pngVerticalResolutionDPIs,
                pngHeightPixels: a.pngHeightPixels,
                pngWidthPixels: a.pngWidthPixels,
                pmsColorsInPngFile: a.pmsColorsInPngFile as PmsColor[],
                personalizationArtworkId: a.id,
                customerId: '',
                fileName: a.fileName,
                originalHeightMM: a.originalHeightMM,
                originalWidthMM: a.originalWidthMM,
                thumbnailUrl: '',
                pngFileUrl: a.pngFileUrl,
                isVectorFileTrueVectorFile: a.isVectorFileTrueVectorFile,
                hasGradient: a.hasGradient,
                hasTransparency: a.hasTransparency,
                hasMasksOrEffects: a.hasMasksOrEffects,
                isFileConvertedToCmykPdf: a.isFileConvertedToCmykPdf,
                areCmykValuesCorrect: a.areCmykValuesCorrect,
                spotColorsInOriginalFile: [],
                customerArtworkId: '',
                processed: '',
                x: xInPX,
                y: yInPX,
                width: widthInPX,
                height: heightInPX,
                rotation: (a.rotationAngle * Math.PI) / 180,
                aspectRatio: widthInPX / heightInPX,
                backgroundImage: URL.createObjectURL(mappedImageBlob),
                smallDetailsResult: smallDetailsPayload,
                isInArea: true,
                isSelected: false,
                alignedTo: '',
                variantArtworkImages: variantArtworkImages,
                kind: 'Logo',
                widthOnProductMM: a.widthWithCropping,
                heightOnProductMM: a.heightWithCropping,
                xOnProductMM: a.distanceFromLeftWithCropping,
                yOnProductMM: a.distanceFromTopWithCropping,
                cmykPdfFileUrl: a.cmykPdfFileUrl ? a.cmykPdfFileUrl : '',
                traditionalTechniquePngFile: traditionalTechniquePngFile,
                fullColorTechniquePngFile: imageBlob,
              } as AnnotationShape);

              actionStore.initializeUserActionForArtwork(a.id, a.userActions);
            }

            for (const t of pos.personalizationTexts) {
              const imageBlob = await convertToBlob(t.pngFileUrl);

              const shapePosition = getShapePositionInPX(
                printArea.printArea,
                t.distanceFromLeft,
                t.distanceFromTop,
                t.width,
                t.height,
                scaledArea
              );

              const xInPX = shapePosition.xInPX;
              const yInPX = shapePosition.yInPX;
              const widthInPX = shapePosition.widthInPX;
              const heightInPX = shapePosition.heightInPX;

              const isLaserConverter = productStore.isLaserConverter(
                positionID,
                pos.productPrintTechnique.sapCode
              );

              let smallDetailsPayload: SmallDetailsPayload | undefined =
                undefined;

              if (DoSmallDetailsPart && t.smallDetails) {
                smallDetailsPayload = {
                  negativeDetailsLoss: t.smallDetails.negativeLoss,
                  positiveDetailsLoss: t.smallDetails.positiveLoss,
                  status: t.smallDetails.status,
                  approvedAt: t.smallDetails.approvedAt
                    ? t.smallDetails.approvedAt.toString()
                    : undefined,
                  isCheckValid: true,
                  pngFile: t.smallDetails.fileUrl,
                  pngFileBlob: await convertToBlob(t.smallDetails.fileUrl),
                  calculatedQuality: t.smallDetails.calculatedQuality,
                  id: t.id,
                };
              }
              let colorArray = [];
              if (isLaserConverter) {
                const techniqueId = product?.printTechniques.find(
                  (x) =>
                    x.printTechnique.code === pos.productPrintTechnique.sapCode
                )?.id;

                const laserColor = productStore.laserVariantColor(
                  product?.id as string,
                  techniqueId as string,
                  productStore.selectedVariant.sku
                );
                const { red: r, green: g, blue: b } = laserColor.laserColor;
                colorArray = [r, g, b];
              } else {
                const { red: r, green: g, blue: b } = t.pmsColorInfo;
                colorArray = [r, g, b];
              }

              let variantArtworkImages: VariantArtworkImages[] = [];
              if (isLaserConverter || isDebossing) {
                variantArtworkImages = await Promise.all(
                  t.variantArtworkImages.map(async (vi) => {
                    const imageBlob = await convertToBlob(
                      vi.variantArtworkPngUrl
                    );
                    return {
                      variantColor: vi.variantColor,
                      variantArtworkPng: imageBlob,
                    } as VariantArtworkImages;
                  })
                );
              }

              this.positionShapeMap = [
                ...this.positionShapeMap,
                { id: positionID, logoId: t.id } as Record<string, string>,
              ];

              const newShape = {
                zindex: t.zIndex,
                isRoundLogo: false,
                id: t.id,
                width: widthInPX,
                height: heightInPX,
                x: xInPX,
                y: yInPX,
                rotation: (t.rotationAngle * Math.PI) / 180,
                title: t.text,
                disableInput: true,
                smallDetailsResult: smallDetailsPayload,
                isInArea: true,
                isSelected: false,
                kind: 'Text',
                backgroundImage: URL.createObjectURL(imageBlob),
                pngFile:
                  (variantArtworkImages.find(
                    (v) =>
                      v.variantColor === productStore.selectedVariant.colour
                  )?.variantArtworkPng as Blob) || imageBlob,
                pngFileWithoutConverters: imageBlob,
                xOnProductMM: t.distanceFromLeft,
                yOnProductMM: t.distanceFromTop,
                widthOnProductMM: t.width,
                heightOnProductMM: t.height,
                variantArtworkImages: variantArtworkImages,
                originalWidthMM: t.originalHeightMM,
                originalHeightMM: t.originalWidthMM,
                disableResize: false,
                pdfUrl: t.pdfUrl,
                w2pText: {
                  text: t.text,
                  textHtml: t.textHtml,
                  fontSizePT: Number(t.fontSize),
                  fontSize: t.fontSize * (originalDPI / 72),
                  fontWeight: t.bold ? 'bold' : 'normal',
                  fontStyle: t.italic ? 'italic' : 'normal',
                  fontFamily: t.font,
                  textAlign: t.textAlignement.toLowerCase(),
                  colorName: t.pmsColorInfo.name,
                  color: colorArray,
                  fontVariant: '',
                } as Text,
              } as TextShape;

              // generate the image for old personalizationtext
              this.shapes.push(newShape as AnnotationShape);
            }
          }
          this.shapes.sort((a, b) => a.zindex - b.zindex);
        }
      }
    },
    async initializeStateFromPersonalizationJson(
      personalizationJson: CreatePersonalizationFormWrapper
    ) {
      const productStore = useProductStore();
      const actionStore = useActionsStore();

      const printPositions = personalizationJson.form.newProofForm
        .newProductPrintPositionForms as any;

      for (const pos of printPositions) {
        const product = productStore.data.productPrintPositions.find(
          (p) => p.name === pos.name
        );
        const positionID = product?.id;

        const technique = product?.printTechniques.find(
          (x) => x.printTechnique.code === pos.productPrintTechnique.SAPCode
        );

        if (positionID) {
          const printArea = productStore.getProductPrintPosition(positionID);

          const scaledArea = productStore.printPositionArea(positionID, true);

          const originalDPI = product?.originalImageWebDpi as number;

          let mappedArtworkIds: Record<string, string>[] =
            pos.personalizationArtworks.map((a: any) => {
              return {
                id: positionID,
                logoId: a.id,
              } as Record<string, string>;
            });

          this.positionShapeMap = [
            ...this.positionShapeMap,
            ...mappedArtworkIds,
          ];
          mappedArtworkIds = [];

          if (printArea) {
            const isTraditionalTechnique =
              productStore.isTraditionalTechnique(positionID);

            const isSmallDetailsTechnique =
              productStore.isSmallDetailsTechnique(
                positionID,
                pos.productPrintTechnique.SAPCode
              );

            for (const a of pos.personalizationArtworks) {
              const fullColorBlob = base64StringToBlob(
                personalizationJson.files.find((file) => file.name === a.id)
                  ?.file || '',
                'image/png'
              );
              const traditionalTechniqueBlob = base64StringToBlob(
                personalizationJson.files.find(
                  (file) => file.name === a.id + '-traditional-tech-png'
                )?.file || '',
                'image/png'
              );

              const shapePosition = getShapePositionInPX(
                printArea.printArea,
                a.distanceFromleftWithCropping,
                a.distanceFromTopWithCropping,
                a.widthWithCropping,
                a.heightWithCropping,
                scaledArea
              );

              const xInPX = shapePosition.xInPX;
              const yInPX = shapePosition.yInPX;
              const widthInPX = shapePosition.widthInPX;
              const heightInPX = shapePosition.heightInPX;

              const mappedImageBlob =
                isTraditionalTechnique && traditionalTechniqueBlob.size > 0
                  ? traditionalTechniqueBlob
                  : fullColorBlob;

              const newShape = {
                zindex: a.zIndex ? a.zIndex : 1,
                isRoundLogo: a.isRoundLogo,
                id: a.id,
                pngFile: mappedImageBlob,
                pngFileWithoutConverters: mappedImageBlob,
                title: a.title !== null ? a.title : '',
                originalFileUrl: a.originalFileUrl,
                originalHorizontalResolutionDPIs:
                  a.originalHorizontalResolutionDPIs,
                originalVerticalResolutionDPIs:
                  a.originalVerticalResolutionDPIs,
                pngHorizontalResolutionDPIs:
                  typeof a.pngHorizontalResolutionDPIs === 'number'
                    ? a.pngHorizontalResolutionDPIs
                    : a.pngHorizontalResolutionDPIs
                    ? Number(a.pngHorizontalResolutionDPIs)
                    : 0,
                pngVerticalResolutionDPIs:
                  typeof a.pngVerticalResolutionDPIs === 'number'
                    ? a.pngVerticalResolutionDPIs
                    : a.pngVerticalResolutionDPIs
                    ? Number(a.pngVerticalResolutionDPIs)
                    : 0,
                pngHeightPixels: a.pngHeightPixels,
                pngWidthPixels: a.pngWidthPixels,
                pmsColorsInPngFile: a.pmsColorsInPngFile as PmsColor[],
                personalizationArtworkId: a.id,
                customerId: '',
                fileName: a.fileName,
                originalHeightMM: a.originalHeightMM,
                originalWidthMM: a.originalWidthMM,
                thumbnailUrl: '',
                pngFileUrl: a.pngFileUrl,
                isVectorFileTrueVectorFile: a.isVectorFileTrueVectorFile,
                hasGradient: a.hasGradient,
                hasTransparency: a.hasTransparency,
                hasMasksOrEffects: a.hasMasksOrEffects,
                isFileConvertedToCmykPdf: a.isFileConvertedToCmykPdf,
                areCmykValuesCorrect: a.areCmykValuesCorrect,
                spotColorsInOriginalFile: [],
                customerArtworkId: '',
                processed: '',
                x: xInPX,
                y: yInPX,
                width: widthInPX,
                height: heightInPX,
                rotation:
                  ((a.rotationAngle ? a.rotationAngle : 0) * Math.PI) / 180,
                aspectRatio: widthInPX / heightInPX,
                backgroundImage: URL.createObjectURL(mappedImageBlob),
                smallDetailsResult: isSmallDetailsTechnique
                  ? DefaultSmallDetailsResult
                  : undefined,
                isInArea: true,
                isSelected: false,
                alignedTo: '',
                variantArtworkImages: [],
                kind: 'Logo',
                widthOnProductMM: a.widthWithCropping,
                heightOnProductMM: a.heightWithCropping,
                xOnProductMM: a.distanceFromleftWithCropping,
                yOnProductMM: a.distanceFromTopWithCropping,
                cmykPdfFileUrl: a.cmykPdfFileUrl ? a.cmykPdfFileUrl : '',
                traditionalTechniquePngFile:
                  traditionalTechniqueBlob.size > 0
                    ? traditionalTechniqueBlob
                    : mappedImageBlob,
                fullColorTechniquePngFile: fullColorBlob,
              } as LogoShape;

              this.shapes.push(newShape as AnnotationShape);

              if (technique) {
                await this.applyEffectIfNeeded(
                  positionID,
                  technique.id,
                  newShape as AnnotationShape
                );
              }

              actionStore.initializeUserActionForArtwork(a.id, a.userActions);
            }

            if (pos.personalizationTexts === undefined) {
              pos.personalizationTexts = [];
            }
            for (const t of pos.personalizationTexts) {
              const imageBlob = base64StringToBlob(
                personalizationJson.files.find((file) => file.name === t.id)
                  ?.file || '',
                'image/png'
              );

              const shapePosition = getShapePositionInPX(
                printArea.printArea,
                t.distanceFromLeft,
                t.distanceFromTop,
                t.width,
                t.height,
                scaledArea
              );

              const xInPX = shapePosition.xInPX;
              const yInPX = shapePosition.yInPX;
              const widthInPX = shapePosition.widthInPX;
              const heightInPX = shapePosition.heightInPX;

              let colorArray = [];
              const { red: r, green: g, blue: b } = t.pmsColorInfo;
              colorArray = [r, g, b];

              this.positionShapeMap = [
                ...this.positionShapeMap,
                { id: positionID, logoId: t.id } as Record<string, string>,
              ];

              const newShape = {
                zindex: t.zIndex ? t.zIndex : 1,
                isRoundLogo: false,
                id: t.id,
                width: widthInPX,
                height: heightInPX,
                x: xInPX,
                y: yInPX,
                rotation:
                  ((t.rotationAngle ? t.rotationAngle : 0) * Math.PI) / 180,
                title: t.text,
                disableInput: true,
                smallDetailsResult: isSmallDetailsTechnique
                  ? DefaultSmallDetailsResult
                  : undefined,
                isInArea: true,
                isSelected: false,
                kind: 'Text',
                backgroundImage: URL.createObjectURL(imageBlob),
                pngFile: imageBlob,
                pngFileWithoutConverters: imageBlob,
                xOnProductMM: t.distanceFromLeft,
                yOnProductMM: t.distanceFromTop,
                widthOnProductMM: t.width,
                heightOnProductMM: t.height,
                variantArtworkImages: [],
                originalWidthMM: t.originalHeightMM,
                originalHeightMM: t.originalWidthMM,
                disableResize: true,
                pdfUrl: '',
                w2pText: {
                  text: t.text,
                  textHtml: t.textHtml,
                  fontSizePT: Number(t.fontSize),
                  fontSize: t.fontSize * (originalDPI / 72),
                  fontWeight: t.bold ? 'bold' : 'normal',
                  fontStyle: t.italic ? 'italic' : 'normal',
                  fontFamily: t.font,
                  textAlign: t.textAlignement.toLowerCase(),
                  colorName: t.pmsColorInfo.name,
                  color: colorArray,
                  fontVariant: '',
                } as Text,
              } as TextShape;

              // generate the image for old personalizationtext
              this.shapes.push(newShape as AnnotationShape);

              if (technique) {
                await this.applyEffectIfNeeded(
                  positionID,
                  technique.id,
                  newShape as AnnotationShape
                );
              }
            }
          }
          this.shapes.sort((a, b) => a.zindex - b.zindex);
        }
      }
    },

    removeShapesForPosition(positionId: string) {
      const shapesToDelete = this.positionShapeMap.filter((psm) => {
        return psm.id === positionId;
      });

      shapesToDelete.forEach((x) => {
        this.removeShape(x.logoId);
      });
    },

    removeShape(id: string) {
      this.deselectShape();

      this.shapes = this.shapes.filter((s) => {
        return s.id !== id;
      });

      this.positionShapeMap = this.positionShapeMap.filter((psm) => {
        return psm.logoId !== id;
      });
      this.$patch((state) => {
        state.activeShapeId = this.shapes[this.shapes.length - 1]?.id || '';
      });
    },

    async duplicateShape(shapeId: string) {
      const productStore = useProductStore();
      const shape = this.shapes.find((x) => x.id === shapeId);
      if (shape) {
        shape.isSelected = false;
        const id = uuidv4();
        await this.replicateShape(
          {
            ...shape,
            isSelected: true,
            id: id,
            personalizationArtworkId: id,
            x: shape.x + 30,
            y: shape.y - 30,
          } as AnnotationShape,
          productStore.selectedPrintPosition.id
        );
        this.activeShapeId = id;
        this.checkIsShapeInArea(id);
      }
    },

    deselectShape() {
      const shapeBoxSectionStore = useShapeBoxSectionStore();
      shapeBoxSectionStore.resetIsValidInput();
      const currentIndex = this.shapes.findIndex(
        (s) => s.id === this.activeShapeId
      );

      if (currentIndex !== -1) {
        this.$patch((state) => {
          const current = state.shapes[currentIndex];
          if (current.kind === 'Text' && current.w2pText.text === '') {
            this.shapes = this.shapes.filter((s) => {
              return s.id !== current.id;
            });
            this.positionShapeMap = this.positionShapeMap.filter((psm) => {
              return psm.logoId !== current.id;
            });
          } else {
            state.shapes.splice(currentIndex, 1);
            state.shapes.push({
              ...current,
              isSelected: false,
            });
          }
          state.activeShapeId = '';
        });
      }
    },

    getFiles(
      shapes: AnnotationShape[],
      isSublimationTechnique: boolean
    ): File[] {
      const files: File[] = [];

      shapes.forEach((shape) => {
        if (
          isSublimationTechnique
            ? shape.pngFileWithoutConverters
            : shape.pngFile
        ) {
          const blob = isSublimationTechnique
            ? shape.pngFileWithoutConverters
            : shape.pngFile;
          const file = new File([blob], `${shape.id}.png`, {
            type: 'image/png',
          });
          files.push(file);
        }
      });

      return files;
    },

    async generateProduct3dpreviewUrl(positionId: string): Promise<string> {
      const selectedShapes = this.positionShapes(positionId).filter(
        (x) => x.isInArea
      ) as AnnotationShape[];

      const productStore = useProductStore();
      const printPosition = productStore.getProductPrintPosition(positionId);

      const isSublimationTechnique =
        productStore.isSublimationTechniqueOnPosition(positionId);

      const command: Product3DPreviewCommand = {
        positionName: printPosition?.name as string,
        productName: productStore.getProductName,
        productSKU: productStore.getProductSku,
        variantColor: productStore.selectedVariant.colour,
        printPositionContent: {
          heightInMM: printPosition?.maxHeightMm as number,
          widthInMM: printPosition?.maxWidthMm as number,
          personalizationArtworks: selectedShapes
            .filter((x) => x.kind === 'Logo')
            .map((x) => {
              return {
                distanceFromLeft: x.xOnProductMM,
                distanceFromTop: x.yOnProductMM,
                fileName: `${x.id}.png`,
                height: x.heightOnProductMM,
                width: x.widthOnProductMM,
                rotationAngle: Math.round(
                  ((x.rotation as number) * 180) / Math.PI
                ),
              };
            }),
          personalizationTexts: selectedShapes
            .filter((x) => x.kind === 'Text')
            .map((x) => {
              return {
                distanceFromLeft: x.xOnProductMM,
                distanceFromTop: x.yOnProductMM,
                fileName: `${x.id}.png`,
                height: x.heightOnProductMM,
                width: x.widthOnProductMM,
                rotationAngle: Math.round(
                  ((x.rotation as number) * 180) / Math.PI
                ),
              };
            }),
        },
      };

      const files = this.getFiles(selectedShapes, isSublimationTechnique);

      const result = await FileService.createProduct3DPreview(command, files);

      if (result.data.isRoundItem) {
        const blob = base64StringToBlob(result.data.pngFile, 'image/png');
        return URL.createObjectURL(blob);
      } else {
        return '';
      }
    },

    async generateProductAIpreviewUrl(positionId: string): Promise<string> {
      const selectedShapes = this.positionShapes(positionId).filter(
        (x) => x.isInArea
      ) as AnnotationShape[];

      const productStore = useProductStore();
      const printPosition = productStore.getProductPrintPosition(positionId);

      const command: ProductAIPreviewCommand = {
        positionCode: printPosition?.name as string,
        productSKU: productStore.getProductSku,
        printTechniqueCode: productStore.techniqueSapCodeOnPosition(
          printPosition?.name as string
        ),
        variantColor: productStore.selectedVariant.colour,
        printPositionContent: {
          heightInMM: printPosition?.maxHeightMm as number,
          widthInMM: printPosition?.maxWidthMm as number,
          personalizationArtworks: selectedShapes
            .filter((x) => x.kind === 'Logo')
            .map((x) => {
              return {
                distanceFromLeft: x.xOnProductMM,
                distanceFromTop: x.yOnProductMM,
                fileName: `${x.id}.png`,
                height: x.heightOnProductMM,
                width: x.widthOnProductMM,
                rotationAngle: Math.round(
                  ((x.rotation as number) * 180) / Math.PI
                ),
              };
            }),
          personalizationTexts: selectedShapes
            .filter((x) => x.kind === 'Text')
            .map((x) => {
              return {
                distanceFromLeft: x.xOnProductMM,
                distanceFromTop: x.yOnProductMM,
                fileName: `${x.id}.png`,
                height: x.heightOnProductMM,
                width: x.widthOnProductMM,
                rotationAngle: Math.round(
                  ((x.rotation as number) * 180) / Math.PI
                ),
              };
            }),
        },
      };

      const files = this.getFiles(
        selectedShapes,
        !productStore.isLaserConverter(positionId)
      );

      const result = await FileService.createProductAIPreview(command, files);

      if (result.data.isNanoBananaAvailable && result.data.isPromptFound) {
        const blob = base64StringToBlob(result.data.pngFile, 'image/png');
        return URL.createObjectURL(blob);
      } else {
        return '';
      }
    },

    updateShapePosition(
      shape: AnnotationShape,
      scaledArea: PrintArea,
      productPrintArea: ProductPrintArea,
      positionShape = false,
      recalculateFontSize = false
    ) {
      const originalShapeIdx = this.shapes.findIndex((x) => x.id === shape.id);

      if (originalShapeIdx !== -1) {
        let newShapePosition = {
          x: shape.x,
          y: shape.y,
          width: shape.width,
          height: shape.height,
        };
        if (shape.kind === 'Logo' && positionShape) {
          const productStore = useProductStore();
          const positionId = this.positionShapeMap.find(
            (x) => x.logoId === shape.id
          )?.id;

          if (positionId) {
            const polygonDetails = productStore.polygonDetails(positionId);
            const position = productStore.getProductPrintPosition(positionId);

            if (position) {
              newShapePosition = getCenterShapePosition(
                shape.pngWidthPixels,
                shape.pngHeightPixels,
                position,
                scaledArea,
                polygonDetails
              );
            }
          }
        }
        if (shape.kind === 'Text' && positionShape) {
          if (shape.widthOnProductMM !== 0 && shape.heightOnProductMM !== 0) {
            const productStore = useProductStore();
            const positionId = this.positionShapeMap.find(
              (x) => x.logoId === shape.id
            )?.id;

            if (positionId) {
              const polygonDetails = productStore.polygonDetails(positionId);
              const position = productStore.getProductPrintPosition(positionId);

              if (position) {
                newShapePosition = getCenterShapePosition(
                  shape.width,
                  shape.height,
                  position,
                  scaledArea,
                  polygonDetails
                );

                //Calculate width in percentage, based on area width in mm and text width in mm
                const widthPercentageOfArea =
                  shape.widthOnProductMM / productPrintArea.widthMm;

                //Calculate width in percentage, based on area width in px and text width in px
                const positionOnAreaInPt = getShapePositionInAreainPt(
                  {
                    x: shape.x,
                    y: shape.y,
                    height: shape.height,
                    width: shape.width,
                  },
                  scaledArea
                );

                if (positionOnAreaInPt) {
                  //Calculate scale factor based on diference in widths in percentage
                  const scaleFactorOnNewPosition =
                    (widthPercentageOfArea * 100) / positionOnAreaInPt?.width;

                  //Calculate new width and height in pixels
                  shape.width = scaleFactorOnNewPosition * shape.width;
                  shape.height = scaleFactorOnNewPosition * shape.height;

                  //Use center found with getCenterShapePosition and calculate new x and y
                  const centerX =
                    newShapePosition.x + newShapePosition.width / 2;
                  const centerY =
                    newShapePosition.y + newShapePosition.height / 2;
                  shape.x = centerX - shape.width / 2;
                  shape.y = centerY - shape.height / 2;

                  //Reset newShapePosition with calculated values
                  newShapePosition = {
                    x: shape.x,
                    y: shape.y,
                    width: shape.width,
                    height: shape.height,
                  };
                }
              }
            }
          }
        }

        const positionOnAreaInPt = getShapePositionInAreainPt(
          newShapePosition,
          scaledArea
        );

        if (positionOnAreaInPt) {
          const positionOnProductInMM = getShapePositionOnProductInMM(
            positionOnAreaInPt,
            productPrintArea,
            newShapePosition.width / newShapePosition.height
          );

          if (positionOnProductInMM) {
            this.$patch((state) => {
              const current = state.shapes[originalShapeIdx];
              // Recalculate new font size in pt based on new widthOnProductMM and heightOnProductMM for text shapes
              let updateFontSize = false;
              let newFontSizePt = 0;
              if (
                recalculateFontSize &&
                current.kind === 'Text' &&
                current.w2pText
              ) {
                newFontSizePt = recalculateFontSizeInPt(
                  current.w2pText.fontSizePT,
                  current.widthOnProductMM,
                  current.heightOnProductMM,
                  positionOnProductInMM.widthOnProductMM,
                  positionOnProductInMM.heightOnProductMM
                );

                updateFontSize = newFontSizePt !== current.w2pText.fontSizePT;
              }

              state.shapes.splice(originalShapeIdx, 1, {
                ...current,
                isInArea: positionShape ? true : current.isInArea,
                x: newShapePosition.x,
                y: newShapePosition.y,
                width: newShapePosition.width,
                height: newShapePosition.height,
                xOnProductMM: positionOnProductInMM.xOnProductMM,
                yOnProductMM: positionOnProductInMM.yOnProductMM,
                widthOnProductMM: positionOnProductInMM.widthOnProductMM,
                heightOnProductMM: positionOnProductInMM.heightOnProductMM,
                rotation: positionShape ? 0 : current.rotation,
                ...(current.kind === 'Logo' && {
                  pngVerticalResolutionDPIs: calculateDpi(
                    current.pngHeightPixels,
                    positionOnProductInMM.heightOnProductMM
                  ),
                  pngHorizontalResolutionDPIs: calculateDpi(
                    current.pngWidthPixels,
                    positionOnProductInMM.widthOnProductMM
                  ),
                }),
                ...(current.kind === 'Text' && {
                  aspectRatio:
                    positionOnProductInMM.widthOnProductMM /
                    positionOnProductInMM.heightOnProductMM,
                }),
                ...(current.kind === 'Text' &&
                  updateFontSize && {
                    w2pText: {
                      ...current.w2pText,
                      fontSizePT: newFontSizePt,
                    },
                    triggerPdfGeneration: true,
                  }),
                isOnRecommendedPrintPosition:
                  shape.isOnRecommendedPrintPosition,
              });
            });
          }
        }
      }
    },

    setSmallDetailsCheckToInvalid2(shapeId: string) {
      const currentShapeIndex = _findIndex(this.shapes, {
        id: shapeId,
      });
      const activeShape = this.shapes[currentShapeIndex];

      const smallDetails = activeShape.smallDetailsResult
        ? { ...activeShape.smallDetailsResult, isCheckValid: false }
        : undefined;

      if (currentShapeIndex > -1) {
        this.$patch((state) => {
          activeShape.smallDetailsResult = smallDetails;
        });
      }
    },

    setSmallDetailsCheckToInvalid(shapeId: string) {
      const currentShapeIndex = _findIndex(this.shapes, {
        id: shapeId,
      });
      const activeShape = this.shapes[currentShapeIndex];

      const smallDetails = activeShape.smallDetailsResult
        ? { ...activeShape.smallDetailsResult, isCheckValid: false }
        : undefined;

      if (currentShapeIndex > -1) {
        this.$patch((state) => {
          state.shapes.splice(currentShapeIndex, 1, {
            ...state.shapes[currentShapeIndex],
            smallDetailsResult: smallDetails,
          });
        });
      }
    },

    setSmallDetailsCheckToDefault(shapeId: string) {
      const currentShapeIndex = _findIndex(this.shapes, {
        id: shapeId,
      });

      if (currentShapeIndex > -1) {
        this.$patch((state) => {
          state.shapes.splice(currentShapeIndex, 1, {
            ...state.shapes[currentShapeIndex],
            smallDetailsResult: {
              ...DefaultSmallDetailsResult,
              isCheckValid: false,
            },
          });
        });
      }
    },

    resetSmallDetailsResult(shapeId: string, isSmallDetailsTech: boolean) {
      const shapeIdx = this.shapes.findIndex((p) => p.id === shapeId);

      const smallDetails = mapSmallDetailsPayload(
        this.shapes[shapeIdx],
        isSmallDetailsTech
      );

      this.shapes.splice(shapeIdx, 1, {
        ...this.shapes[shapeIdx],
        smallDetailsResult: smallDetails,
      });
    },

    transformShape(key: string, value: number) {
      const currentShapeIndex = _findIndex(this.shapes, {
        id: this.activeShapeId,
      });

      const activeShape = this.shapes[currentShapeIndex];

      if (activeShape.kind === 'Logo' && key === 'width') {
        const newHeightMm = determineNewHeightMm(
          activeShape.width,
          activeShape.height,
          value
        );
        const newWidthPx = determineNewWidthPx(
          activeShape.width,
          activeShape.widthOnProductMM,
          value
        );
        const newHeightPx = determineNewHeightPx(
          activeShape.height,
          activeShape.heightOnProductMM,
          newHeightMm
        );

        this.setSmallDetailsCheckToInvalid(activeShape.id);

        if (currentShapeIndex > -1) {
          this.$patch((state) => {
            state.shapes.splice(currentShapeIndex, 1, {
              ...state.shapes[currentShapeIndex],
              width: newWidthPx,
              height: newHeightPx,
              widthOnProductMM: value,
              heightOnProductMM: newHeightMm,
            });
          });
        }
      }

      if (activeShape.kind === 'Logo' && key === 'height') {
        const newWidthMm = determineNewWidthMm(
          activeShape.width,
          activeShape.height,
          value
        );
        const newWidthPx = determineNewWidthPx(
          activeShape.width,
          activeShape.widthOnProductMM,
          newWidthMm
        );
        const newHeightPx = determineNewHeightPx(
          activeShape.height,
          activeShape.heightOnProductMM,
          value
        );

        this.setSmallDetailsCheckToInvalid(activeShape.id);

        if (currentShapeIndex > -1) {
          this.$patch((state) => {
            state.shapes.splice(currentShapeIndex, 1, {
              ...state.shapes[currentShapeIndex],
              height: newHeightPx,
              width: newWidthPx,
              widthOnProductMM: newWidthMm,
              heightOnProductMM: value,
            });
          });
        }
      }
      if (key === 'rotation') {
        const rotation = (value * Math.PI) / 180;
        this.$patch((state) => {
          state.shapes.splice(currentShapeIndex, 1, {
            ...state.shapes[currentShapeIndex],
            rotation,
          });
        });
      }

      this.checkIsShapeInArea(this.activeShapeId);
    },
    async makeLogoRound() {
      const productStore = useProductStore();
      const actionsStore = useActionsStore();

      const currentShapeIndex = _findIndex(this.shapes, {
        id: this.activeShapeId,
      });
      const activeShape = this.shapes[currentShapeIndex];

      const command: DoRoundCroppingCommand = {
        croppingAreaAspectRatioX: '1',
        croppingAreaAspectRatioY:
          (productStore.activePosition.printArea?.heightMm as number) /
          (productStore.activePosition.printArea?.widthMm as number),
      };
      try {
        if (
          activeShape.kind === 'Logo' &&
          productStore.activePosition.id &&
          productStore.selectedPrintPosition.techniqueId
        ) {
          const { data } = await FileService.roundCropping(
            activeShape.pngFileWithoutConverters as Blob,
            command
          );

          const isTraditionalTechnique = productStore.isTraditionalTechnique();

          const secondPng = isTraditionalTechnique
            ? activeShape.fullColorTechniquePngFile
            : activeShape.traditionalTechniquePngFile;
          let secondCroppedPng = '';
          if (secondPng !== '') {
            const res2 = await FileService.roundCropping(secondPng, command);
            secondCroppedPng = res2.data.pngFile;
          }
          const fullColorPng = isTraditionalTechnique
            ? secondCroppedPng
            : data.pngFile;
          let traditionalTechniquePng = '';
          if (
            activeShape.traditionalTechniquePngFile &&
            activeShape.traditionalTechniquePngFile !== ''
          ) {
            traditionalTechniquePng = isTraditionalTechnique
              ? data.pngFile
              : secondCroppedPng;
          }

          const affectedShape = this.shapes[currentShapeIndex];

          const smallDetailsResult = affectedShape.smallDetailsResult
            ? {
                ...affectedShape.smallDetailsResult,
                isCheckValid: false,
              }
            : undefined;

          //Calculate position and size that will fill area:
          const {
            originalImageWebDpi,
            originalImageWidthPx,
            originalImageDpi,
          } = productStore.activePosition;
          const webImageWidthPx =
            originalImageWidthPx &&
            originalImageWebDpi &&
            originalImageDpi &&
            (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;
          const rectLineWidth =
            (lineWidthV2 * (webImageWidthPx as number)) / 100;

          const scaledArea = productStore.printPositionArea(
            productStore.activePosition.id,
            true
          );
          const productArea = productStore.activePosition.printArea;

          if (scaledArea && scaledArea.kind === 'Ellipse') {
            const areaRect = {
              x: scaledArea.x - scaledArea.rx + rectLineWidth / 2,
              y: scaledArea.y - scaledArea.ry + rectLineWidth / 2,
              w: scaledArea.rx * 2 - rectLineWidth,
              h: scaledArea.ry * 2 - rectLineWidth,
            };

            let calcArtworkWidth = 0;
            let calcArtworkHeight = 0;
            const CORRECTION_FACTOR = 0;
            const artworkRatio = data.newWidth / data.newHeight;
            const areaRatio = areaRect.w / areaRect.h;
            // Artwork is more landscape than area - fit to width
            if (artworkRatio > areaRatio) {
              calcArtworkWidth = areaRect.w - CORRECTION_FACTOR;
              calcArtworkHeight =
                (calcArtworkWidth / data.newWidth) * data.newHeight;
            }
            // Artwork is more portrait than area - fit to height
            else {
              calcArtworkHeight = areaRect.h - CORRECTION_FACTOR;
              calcArtworkWidth =
                (calcArtworkHeight / data.newHeight) * data.newWidth;
            }

            //Calculate percentage croppend from each edge
            actionsStore.createCropAction(this.activeShapeId, {
              left:
                data.newWidth == activeShape.pngWidthPixels
                  ? 0
                  : ((activeShape.pngWidthPixels - data.newWidth / 2) /
                      activeShape.pngWidthPixels) *
                    100,
              right:
                data.newWidth == activeShape.pngWidthPixels
                  ? 0
                  : ((activeShape.pngWidthPixels - data.newWidth / 2) /
                      activeShape.pngWidthPixels) *
                    100,
              top:
                data.newHeight == activeShape.pngHeightPixels
                  ? 0
                  : ((activeShape.pngHeightPixels - data.newHeight / 2) /
                      activeShape.pngHeightPixels) *
                    100,
              bottom:
                data.newHeight == activeShape.pngHeightPixels
                  ? 0
                  : ((activeShape.pngHeightPixels - data.newHeight / 2) /
                      activeShape.pngHeightPixels) *
                    100,
            });

            activeShape.isRoundLogo = true;

            this.$patch((state) => {
              state.shapes.splice(currentShapeIndex, 1, {
                ...activeShape,
                backgroundImage: URL.createObjectURL(
                  base64StringToBlob(data.pngFile, 'image/png')
                ),
                pngFile: base64StringToBlob(data.pngFile, 'image/png'),
                pngFileWithoutConverters: base64StringToBlob(
                  data.pngFile,
                  'image/png'
                ),
                fullColorTechniquePngFile: base64StringToBlob(
                  fullColorPng,
                  'image/png'
                ),
                traditionalTechniquePngFile:
                  traditionalTechniquePng !== ''
                    ? base64StringToBlob(traditionalTechniquePng, 'image/png')
                    : '',
                pngHeightPixels: data.newHeight,
                pngWidthPixels: data.newWidth,
                width: calcArtworkWidth,
                height: calcArtworkHeight,
                aspectRatio: calcArtworkWidth / calcArtworkHeight,
                smallDetailsResult,
                x: areaRect.x + lineWidthV2,
                y: areaRect.y + lineWidthV2,
              });
            });
            const updatedShape = this.shapes[currentShapeIndex];
            this.updateShapePosition(updatedShape, scaledArea, productArea);
          }
        }
      } catch (error) {
        console.log('An error occurred', error);
      }
    },
    async changeLogoBackground(proccessingType: string) {
      const productStore = useProductStore();
      const actionsStore = useActionsStore();
      const command: SetArtworkBackgroundCommand = {
        isFullBackground: proccessingType === 'fullBackground',
        color: 2,
      };

      try {
        const activeShape = this.shapes.find(
          (x) => x.id === this.activeShapeId
        );
        if (activeShape && activeShape.kind === 'Logo') {
          const { data } = await FileService.changeLogoBackground(
            activeShape.pngFileWithoutConverters,
            command
          );

          const isTraditionalTechnique = productStore.isTraditionalTechnique();

          const secondPng = isTraditionalTechnique
            ? activeShape.fullColorTechniquePngFile
            : activeShape.traditionalTechniquePngFile;
          let secondPngWithoutBackground = '';
          if (secondPng !== '') {
            const res2 = await FileService.changeLogoBackground(
              secondPng,
              command
            );
            secondPngWithoutBackground = res2.data.pngFile;
          }

          const whiteColor: PmsColor = {
            red: 255,
            green: 255,
            blue: 255,
            colorFuzz: 20,
            originalName: '',
            isOriginalSpotColor: false,
            name: 'Pure White',
            originalBlack: 0,
            originalCyan: 0,
            originalMagenta: 0,
            originalYellow: 0,
          };

          const artworkColors = activeShape.pmsColorsInPngFile;

          if (proccessingType === 'fullBackground') {
            actionsStore.createRemoveBackgroundFullAction(
              this.activeShapeId,
              whiteColor
            );
          }

          if (proccessingType !== 'fullBackground') {
            actionsStore.createRemoveBackgroundOutsideImageAction(
              this.activeShapeId,
              whiteColor
            );
          }

          if (data.isAllWhiteRemoved) {
            const whiteColorIndex = artworkColors.findIndex(
              (c) => c.name === whiteColor.name
            );
            if (whiteColorIndex !== -1) {
              artworkColors.splice(whiteColorIndex, 1);
            }
          }

          if (artworkColors.length !== 0) {
            this.updateArtworkBackground(
              data.pngFile,
              secondPngWithoutBackground,
              artworkColors,
              data.heightInPx,
              data.widthInPx,
              isTraditionalTechnique
            );

            if (productStore.selectedPrintPosition.techniqueId) {
              await this.applyEffectIfNeeded(
                productStore.selectedPrintPosition.id,
                productStore.selectedPrintPosition.techniqueId,
                activeShape
              );
            }
          } else {
            await this.removeShape(activeShape.id);
          }
        }
      } catch (error) {
        console.log('An error occurred', error);
      }
    },
    async textToImage(
      font: string,
      fontSize: number,
      fontWeight: string,
      fontStyle: string,
      text: string,
      textAlignment: string,
      textColor: string,
      textHtml: string,
      color: PmsColor,
      repositionText: boolean,
      shapeId: string
    ) {
      try {
        const textShapeIndex = _findIndex(this.shapes, {
          id: shapeId,
        });

        const productStore = useProductStore();

        const positionId = productStore.selectedPrintPosition.id;
        const printPosition = productStore.data.productPrintPositions.find(
          (x) => x.id === positionId
        );
        const techniqueId = productStore.unavailablePrintPositions.find(
          (x) => x.positionId === positionId
        )?.technique.id;
        const limitation = printPosition?.printTechniques.find(
          (x) => x.id === techniqueId
        )?.printTechnique.limitation;
        const printTechniqueGroupCode =
          printPosition?.printTechniques.find((x) => x.id === techniqueId)
            ?.printTechnique.group.code ?? '';
        const scaledArea = productStore.printPositionArea(positionId, true);
        const activePosition = productStore.getProductPrintPosition(positionId);
        const productArea = activePosition?.printArea;

        const command: ProcessTextCommand = {
          font: font,
          fontSize: fontSize,
          id: shapeId,
          isBold: fontWeight === 'bold',
          isItalic: fontStyle === 'italic',
          text: text,
          textAlignment: textAlignment as TextAlignment,
          textColor: textColor,
          productTechniqueGroupCode: printTechniqueGroupCode,
        };

        const { data } = await FileService.textToImage(command);

        this.$patch((state) => {
          const current = state.shapes[textShapeIndex];

          if (current.kind === 'Text') {
            state.shapes.splice(textShapeIndex, 1, {
              ...current,
              backgroundImage: URL.createObjectURL(
                base64StringToBlob(data.pngFile, 'image/png')
              ),
              pngFile: base64StringToBlob(data.pngFile, 'image/png'),
              pngFileWithoutConverters: base64StringToBlob(
                data.pngFile,
                'image/png'
              ),
              pdfUrl: data.pdfUrl,
              height: data.pngHeightPixels,
              width: data.pngWidthPixels,
              originalWidthMM: data.originalWidthMM,
              originalHeightMM: data.originalHeightMM,
              smallDetailsResult: limitation
                ? {
                    ...DefaultSmallDetailsResult,
                    isCheckValid: false,
                  }
                : undefined,
              w2pText: {
                ...current.w2pText,
                fontFamily: font,
                fontSizePT: fontSize,
                fontWeight,
                fontStyle,
                textAlign: textAlignment,
                text: text,
                textHtml: textHtml,
                colorName: textColor,
                color: [color.red, color.green, color.blue],
              },
              triggerPdfGeneration: false,
            });

            const position = positionText(
              state.shapes[textShapeIndex] as TextShape,
              productStore.selectedPrintPosition.id,
              repositionText
            );
            const previous = state.shapes[textShapeIndex];
            state.shapes.splice(textShapeIndex, 1, {
              ...previous,
              height: position.h,
              width: position.w,
              x: current.x,
              y: current.y,
              rotation: current.rotation,
            });

            if (scaledArea && productArea) {
              this.updateShapePosition(
                state.shapes[textShapeIndex],
                scaledArea,
                productArea
              );
            }

            if (productStore.selectedPrintPosition.techniqueId) {
              this.applyEffectIfNeeded(
                productStore.selectedPrintPosition.id,
                productStore.selectedPrintPosition.techniqueId,
                previous
              );
            }
          }
        });
      } catch (error) {
        console.log('An error occurred', error);
      }
    },
    updateArtworkBackground(
      pngFile: string,
      secondPngWithoutBackground: string,
      artworkColors: PmsColor[],
      height: number,
      width: number,
      isTraditionalTechnique: boolean
    ) {
      const index = this.shapes.findIndex(
        (shape: AnnotationShape) => shape.id === this.activeShapeId
      );

      const affectedShape = this.shapes[index];

      let recalculatedWidth = 0;
      let recalculatedHeight = 0;

      if (width >= height) {
        recalculatedWidth = affectedShape.width;
        recalculatedHeight = (affectedShape.width as number) / (width / height);
      } else {
        recalculatedHeight = affectedShape.height as number;
        recalculatedWidth = (affectedShape.height as number) / (height / width);
      }

      this.setSmallDetailsCheckToInvalid(affectedShape.id);

      const widthScaleFactor = affectedShape.width / recalculatedWidth;
      const heightScaleFactor = affectedShape.height / recalculatedHeight;
      const newWidthMM = affectedShape.widthOnProductMM / widthScaleFactor;
      const newHeightMM = affectedShape.heightOnProductMM / heightScaleFactor;

      const fullColorPng = isTraditionalTechnique
        ? secondPngWithoutBackground
        : pngFile;
      let traditionalTechniquePng = '';
      if (secondPngWithoutBackground !== '') {
        traditionalTechniquePng = isTraditionalTechnique
          ? pngFile
          : secondPngWithoutBackground;
      }

      this.$patch((state) => {
        const current = state.shapes[index];
        if (current.kind === 'Logo') {
          state.shapes.splice(index, 1, {
            ...current,
            backgroundImage: URL.createObjectURL(
              base64StringToBlob(pngFile, 'image/png')
            ),
            pngFile: base64StringToBlob(pngFile, 'image/png'),
            pngFileWithoutConverters: base64StringToBlob(pngFile, 'image/png'),
            fullColorTechniquePngFile: base64StringToBlob(
              fullColorPng,
              'image/png'
            ),
            traditionalTechniquePngFile:
              traditionalTechniquePng !== ''
                ? base64StringToBlob(traditionalTechniquePng, 'image/png')
                : '',
            pmsColorsInPngFile: [...artworkColors],
            height: recalculatedHeight,
            width: recalculatedWidth,
            aspectRatio: recalculatedWidth / recalculatedHeight,
            pngWidthPixels: width,
            pngHeightPixels: height,
            widthOnProductMM: newWidthMM,
            heightOnProductMM: newHeightMM,
          });
        }
      });
    },
    async applyEffectIfNeeded(
      positionId: string,
      techniqueId: string,
      shapePayload: AnnotationShape
    ) {
      const productStore = useProductStore();

      if (productStore.isLaserConverter(positionId)) {
        await this.applyLaserConverter(
          shapePayload.id,
          positionId,
          techniqueId
        );
      } else if (productStore.isDebossingTechnique(positionId)) {
        await this.applyDebossingConverter(shapePayload.id);
      } else if (productStore.isSublimationTechnique) {
        await this.applySublimationConverter(shapePayload.id);
      } else if (
        productStore.isEmbroideryConverter(positionId) &&
        EMBROIDERY_CONVERTER_ACTIVE
      ) {
        await this.applyEmbroideryConverter(shapePayload.id);
      } else if (productStore.isPantoneColorTechnique()) {
        // Use V2 pantone converter
        await this.applyPantoneConverterV2(shapePayload.id);
      }
    },
    updateBackground(pngFile: string, shapePayload: AnnotationShape) {
      const shapeIndex = this.shapes.findIndex(
        (shape: AnnotationShape) => shape.id === shapePayload.id
      );
      const current = this.shapes[shapeIndex];
      if (current) {
        this.$patch((state) => {
          state.shapes.splice(shapeIndex, 1, {
            ...current,
            backgroundImage: pngFile,
          });
        });
      }
    },
    async applyPantoneConverterV2(shapeId: string) {
      try {
        const colorManagementStore = useColorsManagementStore();

        const shapePayload = {
          ...this.shapes[this.shapes.findIndex((x) => x.id === shapeId)],
        };
        if (
          shapePayload &&
          shapePayload.kind === 'Logo' &&
          !shapePayload.hasGradient
        ) {
          const mismatchedPmsColors = shapePayload.pmsColorsInPngFile.filter(
            (color) => {
              const pantoneColor = colorManagementStore.availablePmsColors.find(
                (paletteColor) => paletteColor.name === color.name
              );
              return (
                !pantoneColor ||
                pantoneColor.red !== color.red ||
                pantoneColor.green !== color.green ||
                pantoneColor.blue !== color.blue
              );
            }
          );
          // We need to update preview if:
          // 1. Missing pantone colors in current palette - proposed pantone based on ALL palette colors is not present in current palette
          // 2. RGB values are different from the ones in current palette - switch between palettes
          // If pantone colors in shape are exactly the same as in current palette, no need to update preview
          if (mismatchedPmsColors.length > 0) {
            const idx = this.shapes.findIndex((x) => x.id === shapeId);
            shapePayload.variantArtworkImages = [];

            const changeColorsCommand = {
              replaceColors: mismatchedPmsColors.map((x) => {
                const newColorToSet =
                  colorManagementStore.availablePmsColors.find(
                    (paletteColor) => paletteColor.name === x.name
                  );
                return {
                  oldColorToReplace: x,
                  newColorToSet: newColorToSet ? newColorToSet : x,
                };
              }),
            };

            const { data } = await FileService.replaceColorsV2(
              changeColorsCommand,
              [
                shapePayload.pngFileWithoutConverters
                  ? shapePayload.pngFileWithoutConverters
                  : (shapePayload.pngFile as Blob),
              ]
            );

            const updatedPmsColors: PmsColor[] =
              shapePayload.pmsColorsInPngFile.map((color) => {
                if (mismatchedPmsColors.find((c) => c.name === color.name)) {
                  const pantoneColor =
                    colorManagementStore.availablePmsColors.find(
                      (paletteColor) => paletteColor.name === color.name
                    );
                  if (pantoneColor) {
                    return {
                      ...color,
                      red: pantoneColor.red,
                      green: pantoneColor.green,
                      blue: pantoneColor.blue,
                    };
                  } else {
                    return color;
                  }
                }
                return color;
              });
            this.$patch((state) => {
              const current = state.shapes[idx];
              if (current.kind !== 'Logo') return;
              state.shapes.splice(idx, 1, {
                ...current,
                pmsColorsInPngFile: updatedPmsColors,
                pngFile: base64StringToBlob(data[0].pngFile, 'image/png'),
                traditionalTechniquePngFile: base64StringToBlob(
                  data[0].pngFile,
                  'image/png'
                ),
                pngFileWithoutConverters: base64StringToBlob(
                  data[0].pngFile,
                  'image/png'
                ),
              });
            });

            const actionsStore = useActionsStore();
            if (shapePayload && shapePayload.kind === 'Logo') {
              actionsStore.createPantoneConverterAction(
                shapePayload.personalizationArtworkId
              );
            }

            const updatedShape = this.shapes[idx];
            const image = updatedShape.pngFileWithoutConverters;

            if (image) {
              this.updateBackground(
                URL.createObjectURL(image),
                this.shapes[idx]
              );
            }
          }
        }
      } catch (error) {
        console.log('An error occurred', error);
      }
    },

    async applyEmbroideryConverter(shapeId: string) {
      try {
        const productStore = useProductStore();
        const idx = this.shapes.findIndex((x) => x.id === shapeId);
        const shapePayload = { ...this.shapes[idx] };

        const mappedVariants =
          productStore.selectedVariants &&
          productStore.selectedVariants.map((el) => ({
            ...el,
            ...productStore.data.variants.find(
              (v) => el.variantColor == v.colour
            ),
          }));

        shapePayload.variantArtworkImages = [];

        const embroideryEffectsCommand = {
          numberofcolors:
            shapePayload.kind === 'Logo'
              ? shapePayload.pmsColorsInPngFile.length
              : 1,
        };
        const { data } = await FileService.applyEmbroideryEffect(
          shapePayload.pngFileWithoutConverters
            ? shapePayload.pngFileWithoutConverters
            : (shapePayload.pngFile as Blob),
          JSON.stringify(embroideryEffectsCommand)
        );

        await Promise.all(
          mappedVariants.map(async (v) => {
            const variantImages = shapePayload.variantArtworkImages;

            if (shapePayload) {
              this.$patch((state) => {
                state.shapes.splice(idx, 1, {
                  ...state.shapes[idx],
                  variantArtworkImages: [
                    ...shapePayload.variantArtworkImages,
                    {
                      variantColor: v.colour as string,
                      variantArtworkPng: base64StringToBlob(
                        data.pngFile,
                        'image/png'
                      ),
                      leftPercentage: 0,
                      topPercentage: 0,
                      widthPercentage: 0,
                      heightPercentage: 0,
                    },
                  ],

                  ...(productStore.selectedVariant.colour ===
                    v.variantColor && {
                    pngFile: base64StringToBlob(data.pngFile, 'image/png'),
                  }),
                });
              });
              shapePayload.variantArtworkImages = [
                ...variantImages,
                {
                  variantColor: v.colour as string,
                  variantArtworkPng: base64StringToBlob(
                    data.pngFile,
                    'image/png'
                  ),
                  leftPercentage: 0,
                  topPercentage: 0,
                  widthPercentage: 0,
                  heightPercentage: 0,
                },
              ];
            }
          })
        );
        const actionsStore = useActionsStore();
        if (shapePayload && shapePayload.kind === 'Logo') {
          actionsStore.createEmbroideryConverterAction(
            shapePayload.personalizationArtworkId
          );
        }
      } catch (error) {
        console.log('An error occurred', error);
      }
    },
    async applyLaserConverter(
      shapeId: string,
      positionId: string,
      techniqueId: string
    ) {
      try {
        const productStore = useProductStore();

        const mappedVariants =
          productStore.selectedVariants &&
          productStore.selectedVariants.map((el) => ({
            ...el,
            ...productStore.data.variants.find(
              (v) => el.variantColor == v.colour
            ),
          }));

        const idx = this.shapes.findIndex((x) => x.id === shapeId);
        const shapePayload = { ...this.shapes[idx] };

        if (shapePayload) {
          shapePayload.variantArtworkImages = [];

          await Promise.all(
            mappedVariants.map(async (v) => {
              const laserConfiguration: LaserConfiguration =
                productStore.getVariantLaserColor(
                  positionId,
                  techniqueId,
                  v.sku as string
                );
              const laserEffectsCommand = {
                laserEffects: [
                  {
                    variantColor: v.variantColor,
                    laserPercentage: laserConfiguration.laserPercentage,
                    laserColor: laserConfiguration.laserColor,
                  },
                ],
              };
              if (laserConfiguration && shapePayload) {
                const { data } = await FileService.applyLaserEffect(
                  shapePayload.pngFileWithoutConverters
                    ? shapePayload.pngFileWithoutConverters
                    : shapePayload.pngFile,
                  JSON.stringify(laserEffectsCommand)
                );

                const variantImages = shapePayload.variantArtworkImages;
                this.$patch((state) => {
                  state.shapes.splice(idx, 1, {
                    ...state.shapes[idx],
                    ...{
                      variantArtworkImages: [
                        ...shapePayload.variantArtworkImages,
                        {
                          variantColor: v.colour as string,
                          variantArtworkPng: base64StringToBlob(
                            data.results[0].pngFile,
                            'image/png'
                          ),
                          leftPercentage: 0,
                          topPercentage: 0,
                          widthPercentage: 0,
                          heightPercentage: 0,
                        },
                      ],
                    },

                    ...(productStore.selectedVariant.colour ===
                      v.variantColor && {
                      pngFile: base64StringToBlob(
                        data.results[0].pngFile,
                        'image/png'
                      ),
                    }),
                  });
                });
                shapePayload.variantArtworkImages = [
                  ...variantImages,
                  {
                    variantColor: v.colour as string,
                    variantArtworkPng: base64StringToBlob(
                      data.results[0].pngFile,
                      'image/png'
                    ),
                    leftPercentage: 0,
                    topPercentage: 0,
                    widthPercentage: 0,
                    heightPercentage: 0,
                  },
                ];
              }
            })
          );

          const actionsStore = useActionsStore();

          if (shapePayload && shapePayload.kind === 'Logo') {
            actionsStore.createLaserConverterAction(
              shapePayload.personalizationArtworkId
            );
          }
        }
      } catch (error) {
        console.log('An error occurred', error);
      }
    },
    async applyDebossingConverter(shapeId: string) {
      try {
        const productStore = useProductStore();
        const mappedVariants =
          productStore.selectedVariants &&
          productStore.selectedVariants.map((el) => ({
            ...el,
            ...productStore.data.variants.find(
              (v) => el.variantColor == v.colour
            ),
          }));
        const idx = this.shapes.findIndex((x) => x.id === shapeId);
        const shapePayload = { ...this.shapes[idx] };

        if (shapePayload) {
          shapePayload.variantArtworkImages = [];

          await Promise.all(
            mappedVariants.map(async (v) => {
              if (shapePayload) {
                const variantImage = v.images?.find(
                  (i) =>
                    i.printPositionID == productStore.selectedPrintPosition.id
                );
                if (variantImage && variantImage.mostUsedColor) {
                  const { data } = await FileService.applyDebossingEffect(
                    shapePayload.pngFileWithoutConverters
                      ? shapePayload.pngFileWithoutConverters
                      : (shapePayload.pngFile as Blob),
                    JSON.stringify({
                      debossingEffects: [
                        {
                          colorHex: variantImage.mostUsedColor,
                          variantColor: v.variantColor,
                        },
                      ],
                    })
                  );

                  const variantImages = shapePayload.variantArtworkImages;
                  this.$patch((state) => {
                    state.shapes.splice(idx, 1, {
                      ...state.shapes[idx],
                      ...{
                        variantArtworkImages: [
                          ...shapePayload.variantArtworkImages,
                          {
                            variantColor: v.colour as string,
                            variantArtworkPng: base64StringToBlob(
                              data.results[0].pngFile,
                              'image/png'
                            ),
                            leftPercentage: 0,
                            topPercentage: 0,
                            widthPercentage: 0,
                            heightPercentage: 0,
                          },
                        ],
                      },

                      ...(productStore.selectedVariant.colour ===
                        v.variantColor && {
                        pngFile: base64StringToBlob(
                          data.results[0].pngFile,
                          'image/png'
                        ),
                      }),
                    });
                  });
                  shapePayload.variantArtworkImages = [
                    ...variantImages,
                    {
                      variantColor: v.colour as string,
                      variantArtworkPng: base64StringToBlob(
                        data.results[0].pngFile,
                        'image/png'
                      ),
                      leftPercentage: 0,
                      topPercentage: 0,
                      widthPercentage: 0,
                      heightPercentage: 0,
                    },
                  ];
                }
              }
            })
          );

          const actionsStore = useActionsStore();
          if (shapePayload) {
            if (shapePayload.kind === 'Logo') {
              actionsStore.createDebossingConverterAction(
                shapePayload.personalizationArtworkId
              );
            }
          }
        }
      } catch (error) {
        console.log('An error occurred', error);
      }
    },
    async applySublimationConverter(shapeId: string) {
      try {
        const productStore = useProductStore();
        const mappedVariants =
          productStore.selectedVariants &&
          productStore.selectedVariants.map((el) => ({
            ...el,
            ...productStore.data.variants.find(
              (v) => el.variantColor == v.colour
            ),
          }));
        const idx = this.shapes.findIndex((x) => x.id === shapeId);
        const shapePayload = { ...this.shapes[idx] };

        if (shapePayload) {
          shapePayload.variantArtworkImages = [];

          let rotation = ((shapePayload.rotation as number) * 180) / Math.PI;

          if (rotation >= -90 && rotation < 0) {
            rotation = 360 + rotation;
          }
          const rotationDegrees = parseInt(rotation.toFixed());

          await Promise.all(
            mappedVariants.map(async (v) => {
              if (shapePayload) {
                const variantImage = v.images?.find(
                  (i) =>
                    i.printPositionID == productStore.selectedPrintPosition.id
                );
                if (
                  variantImage &&
                  productStore.activePosition.originalImageUrl
                ) {
                  const backgroundBlob = await convertToBlob(
                    variantImage.imageWebUrl
                  );
                  const { data } = await FileService.applySublimationEffect(
                    JSON.stringify({
                      artworkTopMM: shapePayload.yOnProductMM,
                      artworkLeftMM: shapePayload.xOnProductMM,
                      artworkWidthMM: shapePayload.widthOnProductMM,
                      artworkHeightMM: shapePayload.heightOnProductMM,
                      backgroundWidthMM: variantImage.imageWidthMM,
                      backgroundHeightMM: variantImage.imageHeightMM,
                      rotationDegrees: rotationDegrees,
                    }),
                    shapePayload.pngFileWithoutConverters
                      ? shapePayload.pngFileWithoutConverters
                      : (shapePayload.pngFile as Blob),
                    backgroundBlob
                  );

                  const variantImages = shapePayload.variantArtworkImages;
                  this.$patch((state) => {
                    state.shapes.splice(idx, 1, {
                      ...state.shapes[idx],
                      ...{
                        variantArtworkImages: [
                          ...shapePayload.variantArtworkImages,
                          {
                            variantColor: v.colour as string,
                            variantArtworkPng: base64StringToBlob(
                              data.pngFile,
                              'image/png'
                            ),
                            leftPercentage: data.leftPercentage as number,
                            topPercentage: data.topPercentage as number,
                            widthPercentage: data.widthPercentage as number,
                            heightPercentage: data.heightPercentage as number,
                          },
                        ],
                      },

                      ...(productStore.selectedVariant.colour ===
                        v.variantColor && {
                        pngFile: base64StringToBlob(data.pngFile, 'image/png'),
                      }),
                    });
                  });
                  shapePayload.variantArtworkImages = [
                    ...variantImages,
                    {
                      variantColor: v.colour as string,
                      variantArtworkPng: base64StringToBlob(
                        data.pngFile,
                        'image/png'
                      ),
                      leftPercentage: data.leftPercentage as number,
                      topPercentage: data.topPercentage as number,
                      widthPercentage: data.widthPercentage as number,
                      heightPercentage: data.heightPercentage as number,
                    },
                  ];
                }
              }
            })
          );

          const actionsStore = useActionsStore();
          if (shapePayload) {
            if (shapePayload.kind === 'Logo') {
              actionsStore.createSublimationConverterAction(
                shapePayload.personalizationArtworkId
              );
            }
          }
        }
      } catch (error) {
        console.log('An error occurred', error);
      }
    },
    async addTextPayload(shapePayload: AnnotationShape) {
      if (shapePayload.kind === 'Text') {
        const productStore = useProductStore();

        shapePayload.w2pText.text = '';
        shapePayload.w2pText.textHtml = '';

        const positionId = productStore.selectedPrintPosition.id;
        const printPosition = productStore.data.productPrintPositions.find(
          (x) => x.id === positionId
        );
        if (printPosition) {
          const colorManagementStore = useColorsManagementStore();
          if (colorManagementStore.lastPalette !== 'ALL') {
            shapePayload.w2pText.colorName =
              colorManagementStore.availablePmsColors[0].name;
            const { red, green, blue } =
              colorManagementStore.availablePmsColors[0];
            shapePayload.w2pText.color = [red, green, blue];
          }
        }
        this.shapes.push(shapePayload);
        this.selectShape(shapePayload.id);
        this.positionShapeMap.push({
          logoId: shapePayload.id,
          id: positionId,
        });

        this.checkIsShapeInArea(shapePayload.id);
      }
    },
    selectShape(shapeId: string) {
      const shapeBoxSectionStore = useShapeBoxSectionStore();
      shapeBoxSectionStore.resetIsValidInput();
      this.activeShapeId = shapeId;
    },
    async replaceColorV2(
      from: PmsColor,
      to: PmsColor,
      isOnlyForActiveShape = false
    ) {
      const productStore = useProductStore();
      const actionsStore = useActionsStore();

      const textPrintPositionShapes = isOnlyForActiveShape
        ? this.shapes.filter((x) => x.id === this.activeShapeId)
        : this.shapes
            .filter((shape) =>
              this.positionShapeMap.some(
                (psm) =>
                  psm.id === productStore.selectedPrintPosition.id &&
                  psm.logoId === shape.id
              )
            )
            .filter(textShapes)
            .filter(
              (x) => x.kind === 'Text' && x.w2pText.colorName === from.name
            );

      textPrintPositionShapes.map((t) => {
        const textShapeIndex = _findIndex(this.shapes, {
          id: t.id,
        });

        this.$patch((state) => {
          const current = state.shapes[textShapeIndex];
          if (current.kind === 'Text') {
            this.textToImage(
              current.w2pText.fontFamily,
              current.w2pText.fontSizePT,
              current.w2pText.fontWeight,
              current.w2pText.fontStyle,
              current.w2pText.text,
              current.w2pText.textAlign,
              to.name,
              current.w2pText.textHtml,
              to,
              false,
              current.id
            );
          }
        });
      });

      const nonTextPrintPositionShapes = isOnlyForActiveShape
        ? (this.shapes.filter(
            (x) => x.id === this.activeShapeId
          ) as LogoShape[])
        : (this.shapes
            .filter((shape) =>
              this.positionShapeMap.some(
                (psm) =>
                  psm.id === productStore.selectedPrintPosition.id &&
                  psm.logoId === shape.id
              )
            )
            .filter(nonTextShapes) as LogoShape[]);

      const artworksForProcessing = nonTextPrintPositionShapes.filter((a) => {
        return a.pmsColorsInPngFile.some((c) => c.name.includes(from.name));
      });

      if (artworksForProcessing.length > 0) {
        for (const artwork of artworksForProcessing) {
          const colorsToChange =
            artwork.pmsColorsInPngFile.filter((c) => c.name === from.name) ||
            from;
          let lastBlob = artwork.pngFileWithoutConverters as Blob;

          for (let i = 0; i < colorsToChange.length; i++) {
            const command: ChangeColorsCommand = {
              replaceColors: [
                {
                  oldColorToReplace: colorsToChange[i],
                  newColorToSet: to,
                },
              ],
            };

            try {
              const { data } = await FileService.replaceColorsV2(command, [
                lastBlob,
              ]);

              lastBlob = base64StringToBlob(data[0].pngFile, 'image/png');

              const logoShapeIndex = _findIndex(this.shapes, {
                id: artwork.id,
              });

              const isTraditionalTechnique =
                productStore.isTraditionalTechnique(
                  productStore.selectedPrintPosition.id
                );

              this.$patch((state) => {
                const originalShape = state.shapes[logoShapeIndex];
                if (originalShape.kind === 'Logo') {
                  state.shapes.splice(logoShapeIndex, 1, {
                    ...originalShape,
                    pngFile: base64StringToBlob(data[0].pngFile, 'image/png'),
                    pngFileWithoutConverters: base64StringToBlob(
                      data[0].pngFile,
                      'image/png'
                    ),
                    ...(isTraditionalTechnique &&
                      originalShape.traditionalTechniquePngFile && {
                        traditionalTechniquePngFile: base64StringToBlob(
                          data[0].pngFile,
                          'image/png'
                        ),
                      }),
                    backgroundImage: URL.createObjectURL(
                      base64StringToBlob(data[0].pngFile, 'image/png')
                    ),
                  });
                }
              });
            } catch (error) {
              console.log('An error occurred', error);
            }
          }
        }

        for (const a of artworksForProcessing) {
          const logoShapeIndex = _findIndex(this.shapes, {
            id: a.id,
          });
          const mappedPngFile = {
            id: a.personalizationArtworkId,
            file: a.pngFile as Blob,
          };
          actionsStore.createReplaceColorAction(mappedPngFile.id, from, to);

          const artworkPmsColors = cloneDeep(a.pmsColorsInPngFile);
          to.colorFuzz = 1;

          artworkPmsColors.map((x) => {
            if (x.name === from.name) {
              x.name = to.name;
              x.originalName = to.name;
              x.red = to.red;
              x.green = to.green;
              x.blue = to.blue;
              x.isOriginalSpotColor = from.isOriginalSpotColor;
              x.colorFuzz = to.colorFuzz;
            }
          });

          const uniqueColors = [
            ...new Map(
              artworkPmsColors.map((item) => [item['name'], item])
            ).values(),
          ];

          this.$patch((state) => {
            const originalShape = state.shapes[logoShapeIndex];
            if (originalShape.kind === 'Logo') {
              state.shapes.splice(logoShapeIndex, 1, {
                ...originalShape,
                pmsColorsInPngFile: uniqueColors,
              });
            }
          });
          if (
            productStore.selectedPrintPosition.techniqueId &&
            this.shapes[logoShapeIndex]
          ) {
            await this.applyEffectIfNeeded(
              productStore.selectedPrintPosition.id,
              productStore.selectedPrintPosition.techniqueId,
              this.shapes[logoShapeIndex]
            );
          }
        }
      }
    },

    async removeColorV2(color: PmsColor, isOnlyForActiveShape = false) {
      const productStore = useProductStore();
      const actionsStore = useActionsStore();

      const textPrintPositionShapes = isOnlyForActiveShape
        ? this.shapes.filter(
            (x) => x.id === this.activeShapeId && x.kind === 'Text'
          )
        : this.shapes
            .filter((shape) =>
              this.positionShapeMap.some(
                (psm) =>
                  psm.id === productStore.selectedPrintPosition.id &&
                  psm.logoId === shape.id
              )
            )
            .filter(textShapes)
            .filter(
              (t) => t.kind === 'Text' && t.w2pText.colorName === color.name
            );

      textPrintPositionShapes.map((t) => {
        const textShapeIndex = _findIndex(this.shapes, {
          id: t.id,
        });

        if (textShapeIndex !== -1) {
          this.removeShape(t.id);
        }
      });

      const nonTextPrintPositionShapes = isOnlyForActiveShape
        ? (this.shapes.filter(
            (x) => x.id === this.activeShapeId && x.kind === 'Logo'
          ) as LogoShape[])
        : (this.shapes
            .filter((shape) =>
              this.positionShapeMap.some(
                (psm) =>
                  psm.id === productStore.selectedPrintPosition.id &&
                  psm.logoId === shape.id
              )
            )
            .filter(nonTextShapes) as LogoShape[]);

      const artworksForProcessing = nonTextPrintPositionShapes.filter((a) => {
        return a.pmsColorsInPngFile.some((c) => c.name.includes(color.name));
      });

      if (artworksForProcessing.length > 0) {
        const colorsWithSameName = new Array<PmsColor>();
        artworksForProcessing.forEach((artwork) => {
          artwork.pmsColorsInPngFile.forEach((pmsColor) => {
            if (pmsColor.name == color.name) {
              colorsWithSameName.push(pmsColor);
            }
          });
        });
        const mapedPngFiles = artworksForProcessing.map((a) => {
          return {
            id: a.personalizationArtworkId,
            file: a.pngFileWithoutConverters as Blob,
          };
        });
        let latestBlobs = mapedPngFiles.map((x) => {
          return x.file;
        });
        let latestData: RemoveColorResult[];
        for (let i = 0; i < colorsWithSameName.length; i++) {
          const command: ChangeColorsCommand = {
            replaceColors: [
              {
                oldColorToReplace: colorsWithSameName[i],
                newColorToSet: undefined,
              },
            ],
          };
          try {
            const { data } = await FileService.replaceColorsV2(
              command,
              latestBlobs
            );
            latestBlobs = data.map((x) => {
              return base64StringToBlob(x.pngFile);
            });
            latestData = data;
          } catch (error) {
            console.log('An error occurred', error);
          }
        }
        await Promise.all(
          mapedPngFiles.map(async (x, i) => {
            actionsStore.createRemoveColorAction(x.id, color);

            const shapeIndex = _findIndex(this.shapes, {
              id: x.id,
            });

            const originalShape = this.shapes[shapeIndex];
            if (originalShape && originalShape.kind === 'Logo') {
              const shapePmsColors = originalShape.pmsColorsInPngFile;

              if (shapePmsColors.length === 1) {
                // If it's the last colour then the artwork is removed
                this.removeShape(x.id);
              } else {
                if (shapePmsColors) {
                  const newPmsColors = shapePmsColors.filter(
                    (x) => !x.name.includes(color.name)
                  );
                  const isTraditionalTechnique =
                    productStore.isTraditionalTechnique(
                      productStore.selectedPrintPosition.id
                    );

                  this.$patch((state) => {
                    const widthScaleFactor =
                      originalShape.pngWidthPixels / latestData[i].widthInPx;
                    const heightScaleFactor =
                      originalShape.pngHeightPixels / latestData[i].heightInPx;
                    const newWidth = originalShape.width / widthScaleFactor;
                    const newHeight = originalShape.height / heightScaleFactor;
                    const newWidthMM =
                      originalShape.widthOnProductMM / widthScaleFactor;
                    const newHeightMM =
                      originalShape.heightOnProductMM / heightScaleFactor;
                    state.shapes.splice(shapeIndex, 1, {
                      ...originalShape,
                      pngFile: base64StringToBlob(
                        latestData[i].pngFile,
                        'image/png'
                      ),
                      backgroundImage: URL.createObjectURL(
                        base64StringToBlob(latestData[i].pngFile, 'image/png')
                      ),
                      pngFileWithoutConverters: base64StringToBlob(
                        latestData[i].pngFile,
                        'image/png'
                      ),
                      ...(isTraditionalTechnique &&
                        originalShape.traditionalTechniquePngFile && {
                          traditionalTechniquePngFile: base64StringToBlob(
                            latestData[i].pngFile,
                            'image/png'
                          ),
                        }),
                      aspectRatio: newWidth / newHeight,
                      width: newWidth,
                      height: newHeight,
                      pngWidthPixels: latestData[i].widthInPx,
                      pngHeightPixels: latestData[i].heightInPx,
                      widthOnProductMM: newWidthMM,
                      heightOnProductMM: newHeightMM,
                      pmsColorsInPngFile: newPmsColors,
                      variantArtworkImages: [],
                    });
                  });

                  this.setSmallDetailsCheckToInvalid(x.id);

                  if (
                    productStore.selectedPrintPosition.techniqueId &&
                    this.shapes[shapeIndex]
                  ) {
                    await this.applyEffectIfNeeded(
                      productStore.selectedPrintPosition.id,
                      productStore.selectedPrintPosition.techniqueId,
                      this.shapes[shapeIndex]
                    );
                  }
                }
              }
            }
          })
        );
      }
    },

    checkIsShapeInArea(shapeId: string) {
      const productStore = useProductStore();
      let isInArea = false;
      const positionId = this.positionShapeMap.find(
        (x) => x.logoId === shapeId
      )?.id;

      const position = productStore.printPositions.find(
        (x) => x.id === positionId
      );

      if (position) {
        const printableArea = productStore.printPositionArea(position.id, true);
        const shape = this.shapes.find((x) => x.id === shapeId);

        if (printableArea && shape) {
          const areaRotationInRadians =
            (position.printArea.rotation * Math.PI) / 180;

          isInArea = checkIsInAreaV2(
            shape,
            printableArea,
            areaRotationInRadians
          );
        }
      }

      this.$patch((state) => {
        const idx = state.shapes.findIndex((x) => x.id === shapeId);
        state.shapes.splice(idx, 1, {
          ...state.shapes[idx],
          isInArea,
        });
      });
    },

    reinitializeShapeVariantImage(
      variant: ProductPrintVariant,
      textColor: number[]
    ) {
      const productStore = useProductStore();

      const printPositionShapes = this.shapes.filter((shape) =>
        this.positionShapeMap.some(
          (psm) =>
            psm.id === productStore.selectedPrintPosition.id &&
            psm.logoId === shape.id
        )
      );

      printPositionShapes.forEach((s) => {
        const variantArtworkImage = s.variantArtworkImages?.find(
          (i) => i.variantColor === variant.colour
        );

        s.pngFile =
          variantArtworkImage?.variantArtworkPng || (s.pngFile as Blob);

        if (s.kind === 'Text') {
          s.w2pText = {
            ...s.w2pText,
            color: textColor,
          };
        }
      });
    },

    async applyLanyardOptions(isTile: boolean) {
      if (isTile) {
        await this.executeTile();
      } else {
        await this.executeSingle();
      }
    },

    async executeSingle() {
      const productStore = useProductStore();

      //Sort by x, and than add one per filename
      const currentShapes = [...this.shapes];

      currentShapes.sort((a, b) => a.x - b.x);
      const singleShapePerFilenameOrTextInSelectedPosition =
        [] as AnnotationShape[];

      currentShapes.forEach((shape, index) => {
        if (
          this.positionShapeMap.findIndex(
            (x) =>
              x.logoId === shape.id &&
              x.id === (productStore.activePosition.id as string)
          ) === -1
        ) {
          singleShapePerFilenameOrTextInSelectedPosition.push(shape);
        } else {
          if (shape.kind === 'Logo') {
            if (
              singleShapePerFilenameOrTextInSelectedPosition.findIndex(
                (x) => x.kind === 'Logo' && x.fileName === shape.fileName
              ) == -1
            ) {
              singleShapePerFilenameOrTextInSelectedPosition.push(shape);
            }
          }
          if (shape.kind === 'Text') {
            if (
              singleShapePerFilenameOrTextInSelectedPosition.findIndex(
                (x) =>
                  x.kind === 'Text' && x.w2pText.text === shape.w2pText.text
              ) == -1
            ) {
              singleShapePerFilenameOrTextInSelectedPosition.push(shape);
            }
          }
        }
      });

      this.$patch((state) => {
        state.shapes = singleShapePerFilenameOrTextInSelectedPosition;
        state.positionShapeMap = state.positionShapeMap.filter(
          (x) =>
            singleShapePerFilenameOrTextInSelectedPosition.findIndex(
              (y) => y.id == x.logoId
            ) != -1
        );
      });
    },

    async executeTile() {
      const productStore = useProductStore();

      //Get info about area
      const { originalImageWebDpi, originalImageWidthPx, originalImageDpi } =
        productStore.activePosition;

      const webImageWidthPx =
        (originalImageWidthPx &&
          originalImageWebDpi &&
          originalImageDpi &&
          (originalImageWidthPx * originalImageWebDpi) / originalImageDpi) ||
        1;

      const rectLineWidth = (lineWidthV2 * webImageWidthPx) / 100;

      let minimumDistance = mm2px(
        2,
        productStore.selectedPrintPosition.originalImageWebDpi
      );

      const printPositionArea = productStore.printPositionArea(
        productStore.selectedPrintPosition.id,
        true
      ) as RectangleArea;

      //Move all content of position to the left
      //Create arrays of shapes from current position and shapes from other positions
      const shapesFromActivePosition = [] as AnnotationShape[];
      const shapesFromOtherPositions = [] as AnnotationShape[];

      this.shapes.forEach((shape, index) => {
        if (
          this.positionShapeMap.findIndex(
            (x) =>
              x.logoId === shape.id &&
              x.id === (productStore.activePosition.id as string)
          ) === -1
        ) {
          shapesFromOtherPositions.push(shape);
        } else {
          shapesFromActivePosition.push(shape);
        }
      });

      shapesFromActivePosition.sort((a, b) => a.x - b.x);
      const scaledArea = productStore.printPositionArea(
        productStore.activePosition.id as string,
        true
      );
      let shiftLeft = 0;
      const updatedShapesPositions = [] as AnnotationShape[];
      shapesFromActivePosition.forEach((shape, index) => {
        if (scaledArea) {
          if (shape && index === 0) {
            shiftLeft = shape.x - (printPositionArea.x + rectLineWidth);
            shape.x = printPositionArea.x + rectLineWidth;
          } else {
            shape.x = shape.x - shiftLeft;
          }

          const newShapePosition = {
            x: shape.x,
            y: shape.y,
            width: shape.width,
            height: shape.height,
          };
          const positionOnAreaInPt = getShapePositionInAreainPt(
            newShapePosition,
            scaledArea
          );

          const productArea = productStore.activePosition.printArea;

          if (positionOnAreaInPt) {
            const positionOnProductInMM = getShapePositionOnProductInMM(
              positionOnAreaInPt,
              productArea as ProductPrintArea,
              newShapePosition.width / newShapePosition.height
            );

            if (positionOnProductInMM) {
              shape.xOnProductMM = positionOnProductInMM?.xOnProductMM;
              shape.yOnProductMM = positionOnProductInMM?.yOnProductMM;
            }
          }

          updatedShapesPositions.push({
            ...shape,
          });
        }
      });

      this.$patch((state) => {
        state.shapes = [...updatedShapesPositions, ...shapesFromOtherPositions];
      });

      //Calculate widthOfAllShapesOnSelectedPosition
      let minimumX = 0;
      let maximumX = 0;
      for (let index = 0; index < this.positionShapeMap.length; index++) {
        if (
          this.positionShapeMap[index].id ===
          productStore.selectedPrintPosition.id
        ) {
          const shape = {
            ...this.shapes?.find(
              (a) => a.id === this.positionShapeMap[index].logoId
            ),
          } as AnnotationShape;

          if (shape) {
            if (minimumX === 0) {
              minimumX = shape.x;
            } else {
              if (shape.x < minimumX) {
                minimumX = shape.x;
              }
            }

            if (maximumX === 0) {
              maximumX = shape.x + shape.width;
            } else {
              if (shape.x + shape.width > maximumX) {
                maximumX = shape.x + shape.width;
              }
            }
          }
        }
      }

      const widthOfAllShapesOnSelectedPosition = maximumX - minimumX;

      //Calculate numberOfDuplicateActions
      let usedWidthOfSelectedPrintPosition = maximumX - printPositionArea.x + 2;
      let numberOfDuplicateActions = 0;
      let spreadFinished = false;

      while (spreadFinished === false) {
        if (
          usedWidthOfSelectedPrintPosition +
            minimumDistance +
            widthOfAllShapesOnSelectedPosition <=
          printPositionArea.width
        ) {
          numberOfDuplicateActions++;
          usedWidthOfSelectedPrintPosition =
            usedWidthOfSelectedPrintPosition +
            minimumDistance +
            widthOfAllShapesOnSelectedPosition;
        } else {
          if (numberOfDuplicateActions > 0) {
            const freeSpace =
              printPositionArea.width - usedWidthOfSelectedPrintPosition;
            minimumDistance =
              minimumDistance + freeSpace / numberOfDuplicateActions;
          }

          spreadFinished = true;
        }
      }

      const positionShapeMapLenght = this.positionShapeMap.length;

      //Execute replicateArtwork, using numberOfDuplicateActions
      for (
        let indexOfDuplication = 0;
        indexOfDuplication < numberOfDuplicateActions;
        indexOfDuplication++
      ) {
        for (let i = 0; i < positionShapeMapLenght; i++) {
          if (
            this.positionShapeMap[i].id ===
            productStore.selectedPrintPosition.id
          ) {
            const shape = {
              ...this.shapes?.find(
                (a) => a.id === this.positionShapeMap[i].logoId
              ),
            } as AnnotationShape;

            if (shape) {
              const id = uuidv4();
              this.replicateShape(
                {
                  ...shape,
                  id: id,
                  personalizationArtworkId: id,
                  x:
                    shape.x +
                    widthOfAllShapesOnSelectedPosition *
                      (indexOfDuplication + 1) +
                    minimumDistance * (indexOfDuplication + 1),
                  y: shape.y,
                } as AnnotationShape,
                productStore.selectedPrintPosition.id
              );
            }
          }
        }
      }
    },
    updateShapeStateFromSnapshot(snapshot: ShapeSnapshot) {
      this.$patch((state) => {
        if (snapshot !== undefined) {
          state.activeShapeId = snapshot.activeShapeId;
          state.shapes = snapshot.shapes;
          state.positionShapeMap = snapshot.positionShapeMap;
        }
      });
    },

    revertAppliedEffect(shape: AnnotationShape) {
      const colorsManagement = useColorsManagementStore();
      const idx = this.shapes.findIndex((artwork) => artwork.id === shape.id);
      const shapePayload = { ...this.shapes[idx] };
      this.$patch((state) => {
        if (shapePayload.kind === 'Logo') {
          state.shapes.splice(idx, 1, {
            ...shapePayload,
            pngFile: shapePayload.pngFileWithoutConverters,
            traditionalTechniquePngFile: shapePayload.pngFileWithoutConverters,
            variantArtworkImages: [],
            backgroundImage: shapePayload.pngFileWithoutConverters
              ? URL.createObjectURL(shapePayload.pngFileWithoutConverters)
              : '',
          });
        }

        if (shapePayload.kind === 'Text') {
          const foundColor = colorsManagement.availablePmsColors.find(
            (pms) => pms.name === shapePayload.w2pText.colorName
          ) as PmsColor;

          const { red: r, green: g, blue: b } = foundColor;
          const color = [r, g, b];
          state.shapes.splice(idx, 1, {
            ...shapePayload,
            w2pText: { ...shapePayload.w2pText, color: color },
            pngFile: shapePayload.pngFileWithoutConverters,
            variantArtworkImages: [],
            backgroundImage: shapePayload.pngFileWithoutConverters
              ? URL.createObjectURL(shapePayload.pngFileWithoutConverters)
              : '',
          });
        }
      });
    },
  },
});
